import {
  authCookieName,
  currentScope,
  readCookie,
  SESSION_SCOPE_HEADER,
} from './session-scope';
import axios, { AxiosError, type AxiosRequestConfig } from 'axios';

const RAW_URL = process.env.NEXT_PUBLIC_API_URL ?? '';
const SERVER_BASE_URL = RAW_URL
  ? RAW_URL.endsWith('/api/v1')
    ? RAW_URL
    : `${RAW_URL}/api/v1`
  : 'http://localhost:3001/api/v1';
const BASE_URL = typeof window === 'undefined' ? SERVER_BASE_URL : '/api/v1';

const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor - attach token from cookie (server-side) or header
api.interceptors.request.use((config) => {
  if (typeof document !== 'undefined') {
    // A rota aberta decide de qual sessao e o token: /admin usa a do painel,
    // o resto usa a da barbearia. As duas convivem no mesmo navegador.
    const scope = currentScope();
    const token = readCookie(authCookieName(scope));
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers[SESSION_SCOPE_HEADER] = scope;
  }
  return config;
});

// ─── Sessão com refresh (rotação server-side) ─────────────────────────────────
// O access token (cookie `token`) é curto (auth.web.access_ttl_seconds). Num
// 401, tenta UMA vez POST /auth/refresh (cookie httpOnly `refresh_token`,
// rotacionado pelo backend) e repete a chamada original. Uma única promise de
// refresh é compartilhada entre chamadas concorrentes - evita corrida de
// rotação (refresh usado 2x = reuso detectado = cadeia revogada no servidor).
let refreshInFlight: Promise<boolean> | null = null;

function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post('/api/v1/auth/refresh', {}, { withCredentials: true })
      .then(() => true)
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

/** Rotas de auth não disparam refresh (401 delas = credencial errada/sessão morta). */
const isAuthPath = (url?: string) =>
  !!url && /\/auth\/(login|refresh|logout)/.test(url);

// Response interceptor - 401: tenta refresh 1x e repete; falhou -> login
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      if (original && !original._retried && !isAuthPath(original.url)) {
        original._retried = true;
        if (await refreshSession()) {
          return api(original);
        }
      }
      const scope = currentScope();
      document.cookie = `${authCookieName(scope)}=; Max-Age=0; path=/`;
      // Sem `?redirect`, /login manda o super-admin pro /register (regra do
      // middleware) - por isso o painel volta apontando pra si mesmo.
      window.location.href =
        scope === 'admin' ? '/login?redirect=%2Fadmin' : '/login';
    }
    return Promise.reject(error);
  },
);

// ─── Types ────────────────────────────────────────────────────────────────────

/** Envelope server-side padrao das listas paginadas (espelha shared/pagination). */
export interface PagedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  items?: T[];
  meta?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
}

/**
 * Safely extract an array from an Axios/React-Query response, handling
 * multiple possible API response shapes:
 *   - plain array
 *   - { data: [...] }
 *   - { items: [...] }
 */
export function extractList<T>(axiosData: unknown): T[] {
  if (Array.isArray(axiosData)) return axiosData;
  if (axiosData && typeof axiosData === 'object') {
    const obj = axiosData as Record<string, unknown>;
    if (Array.isArray(obj.items)) return obj.items as T[];
    if (Array.isArray(obj.data)) return obj.data as T[];
  }
  return [];
}

/**
 * Extract pagination meta from various API response shapes.
 */
export function extractMeta(axiosData: unknown): { total: number; page: number; limit: number; totalPages: number } | undefined {
  if (!axiosData || typeof axiosData !== 'object' || Array.isArray(axiosData)) return undefined;
  const obj = axiosData as Record<string, unknown>;
  if (obj.meta && typeof obj.meta === 'object') return obj.meta as { total: number; page: number; limit: number; totalPages: number };
  if (typeof obj.totalPages === 'number') return { total: obj.total as number, page: obj.page as number, limit: obj.limit as number, totalPages: obj.totalPages as number };
  return undefined;
}

/**
 * Extrai a mensagem de erro real do AxiosError retornado pelo backend NestJS.
 * Cobre os tres formatos que aparecem na pratica:
 *   - HttpException com string             -> data.message: 'Cliente nao encontrado'
 *   - ValidationPipe (class-validator)     -> data.message: ['name must...', 'email must...']
 *   - HttpException com payload objeto     -> data.message: 'Telefone ja cadastrado...' (+ outros campos como `code`)
 *
 * Retorna o fallback quando nao consegue extrair (erro de rede, payload nao-JSON, etc).
 */
export function extractApiErrorMessage(error: unknown, fallback: string): string {
  if (!axios.isAxiosError(error)) return fallback;
  const data = error.response?.data as { message?: string | string[] } | undefined;
  if (typeof data?.message === 'string' && data.message.trim()) {
    return data.message;
  }
  if (Array.isArray(data?.message) && data.message.length > 0) {
    return data.message.filter(Boolean).join('. ');
  }
  return fallback;
}

/**
 * Normalize appointment data from API to frontend format.
 * The API returns date+startTime/endTime as separate fields and nested customer/barber,
 * while the frontend expects scheduledAt/endsAt ISO strings and flat customerName/customerPhone.
 */
export function normalizeAppointment(raw: Record<string, unknown>): Record<string, unknown> {
  const a = { ...raw } as Record<string, unknown>;

  // Map date + startTime -> scheduledAt (local ISO string, no Z suffix)
  // startTime/endTime from the API are already in local timezone
  if (!a.scheduledAt && a.date && a.startTime) {
    const dateStr = (a.date as string).split('T')[0];
    a.scheduledAt = `${dateStr}T${a.startTime}:00`;
  }
  if (!a.endsAt && a.date && a.endTime) {
    const dateStr = (a.date as string).split('T')[0];
    a.endsAt = `${dateStr}T${a.endTime}:00`;
  }

  // Flatten customer fields
  const customer = a.customer as Record<string, unknown> | undefined;
  if (customer) {
    if (!a.customerName) a.customerName = customer.name ?? 'Cliente';
    if (!a.customerPhone) a.customerPhone = customer.phone ?? '';
    if (!a.customerEmail && customer.email) a.customerEmail = customer.email;
  }
  if (!a.customerName) a.customerName = 'Cliente';

  // Normalize barber.photoUrl from barber.photo
  const barber = a.barber as Record<string, unknown> | undefined;
  if (barber && barber.photoUrl === undefined && barber.photo !== undefined) {
    barber.photoUrl = barber.photo;
  }

  // Compute totalPrice from service.price if missing
  if (a.totalPrice == null) {
    const service = a.service as Record<string, unknown> | undefined;
    a.totalPrice = service?.price ?? 0;
  }
  if (typeof a.totalPrice === 'string') {
    a.totalPrice = Number(a.totalPrice);
  }
  if (typeof a.financialAmount === 'string') {
    a.financialAmount = Number(a.financialAmount);
  }

  return a;
}

export interface ApiError {
  message: string;
  statusCode: number;
  error?: string;
}

// Auth

export const authApi = {
  login: (data: { email: string; password: string; tenantId?: string }) =>
    api.post<{
      access_token?: string;
      token?: string;
      user?: Record<string, unknown>;
      tenants?: { id: string; slug: string; name: string }[];
      requireTenantSelection?: boolean;
    }>('/auth/login', data),

  register: (data: {
    name: string;
    email: string;
    password: string;
    barbershopName: string;
    slug?: string;
    phone?: string;
    acceptTerms: boolean;
    captchaToken?: string;
  }) =>
    api.post<{
      user: Record<string, unknown>;
      emailVerificationSent?: boolean;
      emailVerificationRequired?: boolean;
    }>('/auth/register', data),

  verifyEmail: (token: string) =>
    api.post<{
      already: boolean;
      user: { id: string; email: string };
      /** Tenant do dono: leva o "Ir para login" pro subdomínio certo. Null se sem tenant. */
      tenant: { slug: string; loginUrl: string } | null;
    }>('/auth/verify-email', { token }),

  forgotPassword: (email: string) =>
    api.post<{ ok: true; message: string }>('/auth/forgot-password', { email }),

  resetPassword: (data: { token: string; password: string }) =>
    api.post<{ ok: true; email: string }>('/auth/reset-password', data),

  /**
   * Inicia o fluxo OAuth do Google. Faz hard-redirect pra GET /auth/google
   * que dispara o consent screen. Backend cuida do callback + decisao de
   * linkar vs finalizar.
   */
  googleAuthStart: () => {
    if (typeof window === 'undefined') return;
    const base = (
      process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:3001'
    ).replace(/\/$/, '');
    const apiBase = base.endsWith('/api/v1') ? base : `${base}/api/v1`;
    window.location.href = `${apiBase}/auth/google`;
  },

  /**
   * Finaliza cadastro Google: troca o exchange token (xt) pelo cookie de
   * sessao + cria Tenant + retorna redirectUrl pro dashboard do tenant.
   */
  finalizeGoogleRegister: (data: {
    xt: string;
    barbershopName: string;
    slug?: string;
    phone?: string;
  }) =>
    api.post<{ redirectUrl: string }>('/auth/google/finalize', data),

  logout: () => api.post('/auth/logout'),

  getMe: () => api.get<Record<string, unknown>>('/auth/me'),
};

// Admin (super-admin)

export type AdminTenantStatus =
  | 'ACTIVE'
  | 'TRIAL'
  | 'PENDING_APPROVAL'
  | 'SUSPENDED'
  | 'CANCELLED';

export type AdminPlanType = string;

/**
 * Ciclos de cobranca. Mensal mora em Plan.priceCents; os longos (semestral/
 * anual) sao precos dinamicos em PlanPrice, definidos pelo Super Admin. Nenhum
 * desconto eh hardcoded - o desconto eh emergente (mensal x preco do ciclo).
 */
export type BillingCycle = 'MONTHLY' | 'SEMIANNUAL' | 'ANNUAL';

/** Preco de um ciclo longo de um plano (linha de PlanPrice). */
export interface PlanCyclePrice {
  cycle: BillingCycle;
  priceCents: number;
  active?: boolean;
}

/**
 * Item da lista de features exibida no card do plano (/pricing). Dinamico e
 * editavel no CRUD de planos do Super Admin - nunca hardcoded no front.
 * `icon` e uma chave semantica resolvida para um icone lucide; `included`
 * controla o tom (incluido x ausente/riscado).
 */
export interface PlanFeature {
  label: string;
  included: boolean;
  icon?: string;
}

export interface AdminPlan {
  id: string;
  code: string;
  name: string;
  description: string | null;
  priceCents: number;
  maxBarbers: number;
  maxAppointmentsPerMonth: number;
  whatsappEnabled: boolean;
  chatbotAiEnabled: boolean;
  active: boolean;
  /** Quando false, plano nao aparece na lista publica do cliente. */
  publiclyListed: boolean;
  /** Destaque comercial ("Mais popular") na lista publica /pricing. */
  featured: boolean;
  sortOrder: number;
  /** Precos dos ciclos longos (semestral/anual) habilitados pelo Super Admin. */
  prices?: PlanCyclePrice[];
  /** Lista de features do card (/pricing), editavel no CRUD. Vazio = derivada. */
  features?: PlanFeature[];
  /** Quantos tenants usam este plano (Tenant.plan = code). 0 = sem uso. */
  tenantsCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminTenantRow {
  id: string;
  slug: string;
  name: string;
  status: AdminTenantStatus;
  /** Tenant de teste (default false). Libera o hard-purge seguro no Super Admin. */
  isTest?: boolean;
  plan: AdminPlanType;
  planName: string | null;
  planPriceCents: number | null;
  /** OVERRIDE explícito de mensalidade (0 = sem override, segue o plano). */
  mrrCents: number;
  /** Mensalidade efetiva: override quando > 0, senão preço vigente do plano. */
  effectiveMrrCents: number;
  /** true quando há override explícito divergente do preço do plano. */
  hasMrrOverride: boolean;
  subscriptionStatus: string | null;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  createdAt: string;
  maxBarbers: number;
  maxAppointmentsPerMonth: number;
  /** Forçar reexibição do onboarding aos clientes deste tenant (Super Admin). */
  mobileForceOnboarding: boolean;
  _count: { users: number; barbers: number };
}

export interface AdminTenantDetail extends AdminTenantRow {
  whatsappKumaAlertsEnabled: boolean;
  whatsappEmailAlertsEnabled: boolean;
  logo: string | null;
  address: string | null;
  /** Endereço estruturado (settings.address) - campos individuais para o
   *  painel super-admin exibir em leitura. null quando nada preenchido. */
  addressDetail: {
    zip: string | null;
    street: string | null;
    number: string | null;
    complement: string | null;
    neighborhood: string | null;
    city: string | null;
    state: string | null;
  } | null;
  latitude: number | null;
  longitude: number | null;
  phone: string | null;
  /** Telefone do atendente humano (settings.attendantPhone). */
  attendantPhone: string | null;
  /** Libera o card "Limpar dados de teste" no painel do dono (Super Admin). Default false. */
  allowTestDataReset: boolean;
  whatsappConnected: boolean;
  whatsappEnabled: boolean;
  /** Tenant aparece na vitrine/descoberta pública do app (busca/proximidade). */
  publicListingEnabled: boolean;
  /** Token Evolution armazenado para este tenant. Pode ser null se nao
   *  provisionado, ou o token real apos resyncWhatsappInstance. Antigos
   *  registros podiam ter slug aqui - re-sync corrige. */
  whatsappInstanceId: string | null;
  chatbotAiEnabled: boolean;
  /** Estado EFETIVO da IA no runtime do chatbot (capability module.chatbot-ai:
   *  bundle do plano + overrides). Pode divergir da flag chatbotAiEnabled
   *  (legada, de exibição) - quando diverge, o painel sinaliza o drift. */
  chatbotAiEffective: boolean;
  /** Override de credencial Asaas para este tenant. null = usa global ativa. */
  paymentGatewayConfigId: string | null;
  /** Override de provedor de OTP deste tenant. null = usa o default global. */
  otpProviderConfigId: string | null;
  /** ID do customer no Asaas (preenchido apos primeiro subscribe). */
  asaasCustomerId: string | null;
  maxBarbers: number;
  maxAppointmentsPerMonth: number;
  appointmentsThisMonth: number;
  /** Barbeiros ATIVOS - consomem quota maxBarbers. */
  barbersActive: number;
  /** Barbeiros INATIVOS - cadastrados mas desabilitados, nao consomem quota. */
  barbersInactive: number;
  owner: {
    id: string;
    email: string;
    name: string | null;
    role: 'OWNER';
    emailVerifiedAt: string | null;
    lastLoginAt: string | null;
    createdAt: string;
  } | null;
  _count: {
    users: number;
    barbers: number;
    customers: number;
    services: number;
    appointments: number;
  };
}

export interface AdminMetrics {
  totalTenants: number;
  activeTenants: number;
  trialingTenants: number;
  suspendedTenants: number;
  pendingTenants: number;
  cancelledTenants: number;
  /** Soma total (active + trial). */
  mrrCents: number;
  /** Soma de MRR de tenants com status=ACTIVE (assinaturas pagas). */
  mrrCentsActive: number;
  /** Soma de MRR de tenants com status=TRIAL (potencial). */
  mrrCentsTrial: number;
  mrrFormatted: string;
  mrrActiveFormatted: string;
  mrrTrialFormatted: string;
  appointmentsThisMonth: number;
  signupsLast30d: number;
  cancelledLast30d: number;
  churnRate30d: number;
}

/**
 * Cobrança individual recebida via webhook do Asaas. Persistida em
 * `Payment` (apps/api/prisma/schema.prisma).
 */
export interface AdminPayment {
  id: string;
  asaasPaymentId: string;
  asaasSubscriptionId: string | null;
  /** PENDING | RECEIVED | CONFIRMED | OVERDUE | REFUNDED | DELETED. */
  status: string;
  /** PIX | BOLETO | CREDIT_CARD | UNDEFINED. */
  billingType: string | null;
  valueCents: number;
  netValueCents: number | null;
  dueDate: string | null;
  paidAt: string | null;
  invoiceUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminAuditEntry {
  id: string;
  superAdminId: string;
  action: string;
  targetTenantId: string | null;
  metadata: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  targetTenant: { slug: string; name: string } | null;
}

/** U9-B - resumo de um arquivo (dump) de tenant. */
/**
 * Política de backup do banco. O painel só entrega por download - persistir
 * artefato é trabalho da infraestrutura, nunca da aplicação.
 */
export interface DatabaseBackupPolicy {
  isProduction: boolean;
  downloadOnly: true;
  reason: string;
}

/** Resultado do reimport. `warnings` carrega o que o operador precisa fazer depois. */
export interface TenantReimportResult {
  tenantId: string;
  tenantSlug: string;
  inserted: Record<string, number>;
  warnings: string[];
}

export interface TenantArchiveCoverage {
  schemaVersion: number;
  assetsIncluded: boolean;
  assetBytes: number;
  tableCounts: Record<string, number>;
  sharedTables: string[];
  skipped: Record<string, string>;
  notArchivedExternal: string[];
}

export interface TenantArchiveSummary {
  id: string;
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  reason: string | null;
  sizeBytes: number;
  recordCount: number;
  status: string;
  createdAt: string;
  downloadedAt: string | null;
  reimportedAt: string | null;
  reimportNotes: string | null;
  /** Null em arquivos gerados antes do formato v2. */
  coverage: TenantArchiveCoverage | null;
  /** false quando o tenant ja foi deletado - o arquivo sobrevive de proposito. */
  tenantExists: boolean;
  /** Status atual do tenant, ou null quando ele nao existe mais. */
  tenantStatus: string | null;
}

export interface AdminListResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}

export interface PlatformConfigRow {
  id: string;
  name: string;
  shortName: string;
  tagline: string;
  logoUrl: string | null;
  iconUrl: string | null;
  primaryColor: string;
  supportEmail: string | null;
  rootDomain: string;
  trialDays: number;
  loginPanelTitle: string;
  loginPanelSubtitle: string;
  loginFeatures: Array<{ title: string; description?: string; icon?: string }>;
  registerPanelTitle: string;
  registerPanelSubtitle: string;
  registerHeaderText: string;
  loginTheme: string;
  /** Exibe barbearias em TRIAL na descoberta dos apps (vitrine). */
  showTrialInDiscovery: boolean;
  /** Raio maximo (km) do slider de proximidade no app do cliente. */
  discoveryRadiusMaxKm: number;
  /** Raio (km) pre-selecionado por padrao na busca por proximidade. */
  discoveryDefaultRadiusKm: number;
  updatedAt: string;
  updatedBy: string | null;
}

export type UpdatePlatformConfigInput = Partial<
  Omit<PlatformConfigRow, 'id' | 'updatedAt' | 'updatedBy'>
>;

export type EvolutionConnectionState =
  | 'open'
  | 'connecting'
  | 'close'
  | 'refused'
  | 'unknown'
  | 'not_found'
  | 'disconnected'
  | 'error';

export type WhatsappNotificationOutcome =
  | 'sent'
  | 'failed-after-retries'
  | 'skipped-no-phone';

export interface AdminWhatsappTenantSnapshot {
  tenantId: string;
  slug: string;
  name: string;
  tenantStatus: AdminTenantStatus;
  whatsappEnabled: boolean;
  whatsappConnected: boolean;
  whatsappInstanceId: string | null;
  liveState: EvolutionConnectionState | null;
  lastCheckAt: string | null;
  lastDisconnectedAt: string | null;
  lastReconnectedAt: string | null;
  consecutiveBadCycles: number;
  lastNotification: { outcome: WhatsappNotificationOutcome; at: string } | null;
}

export interface AdminWhatsappListResponse {
  items: AdminWhatsappTenantSnapshot[];
  total: number;
  healthy: number;
  unhealthy: number;
}

export type PlatformMessageSurface =
  | 'api'
  | 'web-public'
  | 'web-tenant'
  | 'web-admin';

export interface PlatformMessageSource {
  file: string;
  line?: number;
  symbol?: string;
  surface: PlatformMessageSurface;
  route?: string;
}

export type PlatformMessageRenderStyle =
  | 'default'
  | 'bootstrap-basic'
  | 'bootstrap-compact'
  | 'bootstrap-action'
  | 'rich-avatar'
  | 'sticky';

export type PlatformMessagePosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

export interface PlatformMessageRow {
  id: string;
  key: string;
  value: string;
  defaultValue: string;
  category: string;
  description: string | null;
  placeholders: string[];
  scope: string;
  sources: PlatformMessageSource[];
  renderStyle: PlatformMessageRenderStyle | null;
  position: PlatformMessagePosition | null;
  durationMs: number | null;
  /// null = mensagem global. Quando preenchido, esta linha e override
  /// per-tenant (operador editou para esta barbearia especifica).
  tenantId: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

export interface AdminMessagesListResponse {
  items: PlatformMessageRow[];
  total: number;
}

export interface AdminMessagesRoutesResponse {
  /** Mantido por retrocompatibilidade. Use `surfaces` para granularidade. */
  api: Array<{ route: string | null; count: number; keys: string[] }>;
  web: Array<{ route: string | null; count: number; keys: string[] }>;
  /** Rotas separadas por surface granular. Cada surface tem suas
   *  proprias rotas, incluindo a entrada `route === null` para chaves
   *  sem rota HTTP especifica (badges, helpers, componentes). `keys`
   *  e a lista (ordenada) de chaves daquela rota - usada pela UI pra
   *  mostrar preview no tooltip do dropdown. */
  surfaces: {
    api: Array<{ route: string | null; count: number; keys: string[] }>;
    'web-public': Array<{ route: string | null; count: number; keys: string[] }>;
    'web-tenant': Array<{ route: string | null; count: number; keys: string[] }>;
    'web-admin': Array<{ route: string | null; count: number; keys: string[] }>;
  };
  /** Total de mensagens **distintas** por surface (union de keys que
   *  aparecem em qualquer rota). E o numero correto pra "quantas posso
   *  editar aqui". Pode ser MENOR que surfaceRouteSums quando ha overlap. */
  surfaceCounts: {
    api: number;
    'web-public': number;
    'web-tenant': number;
    'web-admin': number;
  };
  /** Soma dos counts de cada rota da surface (= expandir dropdown +
   *  somar). Sempre >= surfaceCounts; a diferenca indica overlap. */
  surfaceRouteSums?: {
    api: number;
    'web-public': number;
    'web-tenant': number;
    'web-admin': number;
  };
  /** Quantas rotas distintas em cada surface (inclui null "Sem rota HTTP"). */
  surfaceRouteCounts?: {
    api: number;
    'web-public': number;
    'web-tenant': number;
    'web-admin': number;
  };
}

export type ReconnectOutcome =
  | 'already-connected'
  | 'success-pending'
  | 'success-connected'
  | 'baileys-dead'
  | 'no-instance'
  | 'instance-not-found'
  | 'error';

export type ReconnectNextAction =
  | 'none'
  | 'wait-and-refresh'
  | 'reset-qr'
  | 'try-again'
  | 'investigate';

export interface AdminWhatsappReconnectResponse {
  outcome: ReconnectOutcome;
  stateBefore: EvolutionConnectionState | null;
  stateAfter: EvolutionConnectionState | null;
  message: string;
  nextAction: ReconnectNextAction;
  details?: {
    httpStatus?: number;
    error?: string;
  };
}

export interface AdminWhatsappResetQrResponse {
  state: string;
  qrcode: string | null;
  pairingCode: string | null;
  connected: boolean;
  message?: string;
}

export interface AdminMonitoringItem {
  id: number;
  name: string;
  type: string; // 'http' | 'push' | 'tcp' | ...
  active: boolean;
  tags: Array<{ name: string; color: string }>;
  // Status do Uptime Kuma: 0=DOWN, 1=UP, 2=PENDING, 3=MAINTENANCE
  lastStatus: 0 | 1 | 2 | 3 | null;
  lastTimeIso: string | null;
  lastMsg: string | null;
  lastPingMs: number | null;
}

export interface AdminMonitoringSnapshot {
  configured: boolean;
  items: AdminMonitoringItem[];
  /**
   * Total de monitores no Kuma sem filtrar por tag. items.length pode
   * ser menor (ex: filtro 7barber). Permite o painel exibir "X de Y" e
   * sinalizar que outros monitores existem fora do escopo do 7barber.
   */
  totalAvailable: number;
  baseUrl: string | null;
  fetchedAtIso: string;
  error?: string;
}

export type AdminHealthSeverity = 'ok' | 'warn' | 'critical';

export interface AdminRecommendedAction {
  kind: 'workflow' | 'scroll' | 'external';
  label: string;
  target: string;
  confirmToken?: string;
}

export interface AdminHealthCheck {
  id: string;
  label: string;
  value: string;
  severity: AdminHealthSeverity;
  hint: string;
  recommendedAction?: AdminRecommendedAction;
}

export type AdminSystemMaintenanceActionKind = 'workflow';

export interface AdminSystemMaintenanceAction {
  actionKey: string;
  operationKind: AdminSystemMaintenanceActionKind;
  workflowFile: string;
  workflowUrl: string;
  confirmToken?: string;
}

export interface AdminSystemMaintenanceThresholds {
  diskWarningPct: number;
  diskCriticalPct: number;
  staleContainersWarningCount: number;
  swarmRestartsWarningCount: number;
  abnormalExitsWarningCount: number;
  incidentsWarningCount: number;
  incidentsCriticalCount: number;
  memoryWarningPct: number;
  unusedImagesWarningCount: number;
  buildCacheWarningMb: number;
  buildCacheKeepMb: number;
  buildkitCacheHostWarningMb: number;
  containerdWarningMb: number;
  backupsWarningMb: number;
  runnerHomeWarningMb: number;
  hostMemoryWarningPct: number;
  hostSwapWarningPct: number;
  memoryCriticalPct: number;
  containerFailuresWarningCount: number;
  containerFailuresCriticalCount: number;
}

export interface AdminSystemMaintenanceRuntimeConfig {
  stackPrefix: string;
  apiContainerNameFilter: string;
  dbContainerNameFilter: string;
  evolutionContainerNameFilter: string;
  evolutionInternalBaseUrl: string;
  composeDir: string;
  dockerDiskMountPath: string;
  defaultDiagnosticTenantSlug: string;
  evolutionInstancesPath: string;
  evolutionDataPath: string;
  evolutionLogSinceMinutes: number;
  evolutionLogTailLines: number;
  apiLogSinceMinutes: number;
  apiLogTailLines: number;
  probeTimeoutSeconds: number;
}

export interface AdminSystemMaintenanceCleanupPolicy {
  evolutionImageRepository: string;
  evolutionPreserveTag: string;
  imagePruneUntilHours: number;
  taskHistoryLimit: number;
  buildCacheKeepMb: number;
}

export interface AdminSystemMaintenanceExternalMonitor {
  monitorKey: string;
  actionKey: string;
  monitorName: string;
  baseUrl: string;
  targetUrl: string;
  acceptedStatusCodes: string[];
  venvPythonPath: string;
}

export interface AdminSystemMaintenanceImageRepository {
  repository: string;
  keepLatestCount: number;
}

export interface AdminSystemMaintenanceProbeTarget {
  probeKey: string;
  actionKey: string;
  path: string;
  requiresEvolutionApiKey: boolean;
}

export interface AdminSystemMaintenanceServiceTarget {
  serviceKey: string;
  serviceName: string;
  containerNameFilter: string;
  monitorEnabled: boolean;
  cleanupEnabled: boolean;
}

export interface AdminSystemMaintenanceConfig {
  snapshotSource: string;
  actionsRepoBaseUrl: string;
  bootstrapWorkflowFile: string;
  bootstrapWorkflowUrl: string;
  collectorScriptSourcePath: string;
  collectorScriptPath: string;
  collectorCronPath: string;
  collectorCronSuppressOutput: boolean;
  collectorIntervalMinutes: number;
  pollIntervalSeconds: number;
  snapshotRetentionDays: number;
  runtime: AdminSystemMaintenanceRuntimeConfig;
  thresholds: AdminSystemMaintenanceThresholds;
  cleanupPolicy: AdminSystemMaintenanceCleanupPolicy | null;
  externalMonitors: AdminSystemMaintenanceExternalMonitor[];
  imageRepositories: AdminSystemMaintenanceImageRepository[];
  probeTargets: AdminSystemMaintenanceProbeTarget[];
  serviceTargets: AdminSystemMaintenanceServiceTarget[];
  actions: AdminSystemMaintenanceAction[];
}

export interface AdminUnusedImage {
  ref: string;
  id: string;
  size: string;
  createdSince: string;
}

export interface AdminAbnormalExit {
  ts: string;
  container: string;
  exitCode: number;
  oomKilled: boolean;
  image: string;
}

/**
 * Classificacao derivada pelo backend (system-health.service.ts).
 * Frontend usa pra filtrar artefatos cosmeticos do rolling update do
 * Docker Swarm e historico antigo de deploys, deixando so o que e
 * acionavel.
 */
export type AdminSwarmTaskKind =
  | 'running'
  | 'failure'
  | 'swarm-artifact'
  | 'historical'
  | 'pending';

export interface AdminSwarmTask {
  service: string;
  name: string;
  state: string;
  stateDetail: string;
  image: string;
  error: string;
  kind?: AdminSwarmTaskKind;
}

export interface AdminMemStat {
  name: string;
  usage: string;
  percent: string;
}

export interface AdminIncidentEntry {
  ts: string;
  kind: string;
  container?: string;
  exitCode?: number;
  oomKilled?: boolean;
  image?: string;
}

export interface AdminSystemHealthSnapshot {
  generatedAtIso: string;
  hostname: string;
  schemaVersion?: number;
  disk: {
    filesystem: string;
    type: string;
    totalMb: number;
    usedMb: number;
    availMb: number;
    usedPct: number;
  };
  containers: {
    running: number;
    exited: number;
    created: number;
    dead: number;
    abnormalExits?: number;
    abnormalExitsList?: AdminAbnormalExit[];
  };
  images: {
    total: number;
    dangling: number;
    unusedCount?: number;
    unusedList?: AdminUnusedImage[];
    unusedEstimate?: number;
  };
  buildCache: { totalMb: number };
  stack7barber: {
    containersTotal: number;
    containersHealthy: number;
    swarmTasks?: AdminSwarmTask[];
    shutdownsRecent?: number;
    failedRecent?: number;
    memStats?: AdminMemStat[];
    highMemCount?: number;
  };
  incidents?: {
    recentCount: number;
    recent: AdminIncidentEntry[];
  };
}

export interface AdminSystemHealthResponse {
  configured: boolean;
  snapshot: AdminSystemHealthSnapshot | null;
  maintenance: AdminSystemMaintenanceConfig | null;
  analysis: {
    overallSeverity: AdminHealthSeverity;
    checks: AdminHealthCheck[];
    cleanupWorkflowUrl: string;
  } | null;
  error?: string;
}

/** Linha de SystemRuntimeConfig editável no super-admin (editor por categoria). */
export interface RuntimeConfigRow {
  key: string;
  value: string;
  valueType: string;
  category: string;
  isPublic: boolean;
  description: string | null;
  updatedAt: string;
}

export const adminApi = {
  metrics: () => api.get<AdminMetrics>('/admin/metrics'),

  listRuntimeConfig: (category = 'mobile') =>
    api.get<RuntimeConfigRow[]>('/admin/runtime-config', { params: { category } }),

  updateRuntimeConfig: (key: string, value: string) =>
    api.patch<RuntimeConfigRow>(`/admin/runtime-config/${encodeURIComponent(key)}`, { value }),

  getPlatformConfig: () =>
    api.get<PlatformConfigRow>('/admin/platform/config'),

  updatePlatformConfig: (data: UpdatePlatformConfigInput) =>
    api.patch<PlatformConfigRow>('/admin/platform/config', data),

  getReservedSubdomains: () =>
    api.get<{ list: string[] }>('/admin/platform/reserved-subdomains'),

  updateReservedSubdomains: (list: string[]) =>
    api.patch<{ list: string[] }>('/admin/platform/reserved-subdomains', { list }),

  listTenants: (params?: {
    status?: AdminTenantStatus;
    plan?: AdminPlanType;
    search?: string;
    page?: number;
    limit?: number;
  }) =>
    api.get<AdminListResponse<AdminTenantRow>>('/admin/tenants', {
      params,
    }),

  /** Lista enxuta para popular dropdowns - sem paginacao, sem
   *  limite arbitrario. Devolve {id, slug, name, plan, status}. */
  listTenantOptions: () =>
    api.get<{
      items: Array<{
        id: string;
        slug: string;
        name: string;
        plan: string;
        status: string;
      }>;
      total: number;
    }>('/admin/tenants/options'),

  listPlans: () => api.get<AdminPlan[]>('/admin/plans'),

  createPlan: (data: Omit<AdminPlan, 'id' | 'createdAt' | 'updatedAt'>) =>
    api.post<AdminPlan>('/admin/plans', data),

  updatePlan: (code: string, data: Partial<Omit<AdminPlan, 'id' | 'code' | 'createdAt' | 'updatedAt'>>) =>
    api.patch<AdminPlan>(`/admin/plans/${code}`, data),

  /** Define o preco de um ciclo longo (SEMIANNUAL/ANNUAL) do plano. */
  setPlanPrice: (
    code: string,
    cycle: 'SEMIANNUAL' | 'ANNUAL',
    data: { priceCents: number; active?: boolean },
  ) => api.put<PlanCyclePrice>(`/admin/plans/${code}/prices/${cycle}`, data),

  /** Remove o preco de um ciclo longo - o ciclo deixa de ser ofertado. */
  deletePlanPrice: (code: string, cycle: 'SEMIANNUAL' | 'ANNUAL') =>
    api.delete<{ deleted: boolean }>(`/admin/plans/${code}/prices/${cycle}`),

  /**
   * Atalho do showcase: aplica um % de desconto a um ciclo longo gerando os
   * PlanPrice de todos os planos pagos publicos de uma vez (SSOT segue em
   * PlanPrice; o % so pre-preenche os valores). Idempotente.
   */
  applyCycleDiscount: (cycle: 'SEMIANNUAL' | 'ANNUAL', data: { percent: number }) =>
    api.post<{
      cycle: 'SEMIANNUAL' | 'ANNUAL';
      percent: number;
      count: number;
      applied: Array<{ code: string; priceCents: number }>;
    }>(`/admin/plans/prices/${cycle}/apply-discount`, data),

  getTenant: (id: string) => api.get<AdminTenantDetail>(`/admin/tenants/${id}`),

  listTenantPayments: (id: string) =>
    api.get<AdminPayment[]>(`/admin/tenants/${id}/payments`),

  createTenant: (data: {
    name: string;
    slug?: string;
    adminEmail: string;
    adminName: string;
    whatsappKumaAlertsEnabled?: boolean;
    whatsappEmailAlertsEnabled?: boolean;
    plan?: AdminPlanType;
    status?: AdminTenantStatus;
  }) =>
    api.post<{
      tenant: AdminTenantRow;
      adminEmail: string;
      tempPassword: string;
    }>('/admin/tenants', data),

  updateTenantStatus: (id: string, data: { status: AdminTenantStatus; reason?: string }) =>
    api.patch<AdminTenantRow>(`/admin/tenants/${id}/status`, data),

  updateTenantWhatsappAlerts: (
    id: string,
    data: { whatsappKumaAlertsEnabled?: boolean; whatsappEmailAlertsEnabled?: boolean },
  ) => api.patch<{ id: string; whatsappKumaAlertsEnabled: boolean; whatsappEmailAlertsEnabled: boolean }>(
    `/admin/tenants/${id}/whatsapp-alerts`, data,
  ),

  // ─── Exclusão definitiva de barbeiro de teste (U12, override do super-admin) ──
  listTenantBarbers: (tenantId: string) =>
    api.get<Barber[]>(`/admin/tenants/${tenantId}/barbers`),

  barberPurgePreview: (tenantId: string, barberId: string) =>
    api.get<BarberPurgePreview>(
      `/admin/tenants/${tenantId}/barbers/${barberId}/purge-preview`,
    ),

  barberPurge: (tenantId: string, barberId: string, reason?: string) =>
    api.delete(`/admin/tenants/${tenantId}/barbers/${barberId}/purge`, {
      params: reason ? { reason } : undefined,
    }),

  /** Marca/desmarca o tenant como de TESTE (libera o hard-purge seguro). */
  setTenantTestFlag: (id: string, isTest: boolean) =>
    api.patch<AdminTenantRow>(`/admin/tenants/${id}/test-flag`, { isTest }),

  /**
   * Liga/desliga a vitrine/descoberta pública do tenant no app (busca por
   * nome/proximidade). Não afeta a entrada direta por QR/link.
   */
  setTenantListing: (id: string, publicListingEnabled: boolean) =>
    api.patch<AdminTenantRow>(`/admin/tenants/${id}/listing`, {
      publicListingEnabled,
    }),

  /** Liga/desliga o "forçar reexibição do onboarding" para os clientes do tenant. */
  setTenantMobileOnboardingForce: (id: string, force: boolean) =>
    api.patch<{ id: string; mobileForceOnboarding: boolean }>(
      `/admin/tenants/${id}/mobile-onboarding-force`,
      { force },
    ),

  /** Libera/bloqueia o card "Limpar dados de teste" (zona de perigo) no painel do dono. */
  setTenantAllowTestDataReset: (id: string, allow: boolean) =>
    api.patch<{ id: string; allowTestDataReset: boolean }>(
      `/admin/tenants/${id}/allow-test-data-reset`,
      { allow },
    ),

  /**
   * Re-sincroniza Tenant.whatsappInstanceId com o token real do Evolution.
   * Idempotente. Necessario antes de renomear tenant para garantir routing
   * de webhook continuar funcionando apos mudanca de slug.
   */
  resyncWhatsappInstance: (id: string) =>
    api.post<{
      tenantId: string;
      slug: string;
      whatsappInstanceId: string;
      changed: boolean;
    }>(`/admin/tenants/${id}/whatsapp/resync`, {}),

  /**
   * Renomeia tenant - altera Tenant.name e/ou Tenant.slug. Super-admin only.
   * Pelo menos um campo deve ser informado. Backend valida colisao e
   * reserved subdomains.
   */
  renameTenant: (
    id: string,
    data: { name?: string; slug?: string; reason?: string },
  ) => api.patch<AdminTenantDetail>(`/admin/tenants/${id}/identity`, data),

  /** Define/corrige a geolocalizacao do tenant (lat/lng + endereco). Auditado. */
  updateTenantLocation: (
    id: string,
    data: { latitude?: number; longitude?: number; address?: string },
  ) => api.patch<AdminTenantDetail>(`/admin/tenants/${id}/location`, data),

  updateTenantBilling: (
    id: string,
    data: {
      plan: string;
      /** Opcional: ausente = backend mantém o status atual (cortesia força
       *  ACTIVE). Transições de ciclo de vida usam updateTenantStatus. */
      status?: AdminTenantStatus;
      trialEndsAt?: string | null;
      currentPeriodEnd?: string | null;
      subscriptionStatus?: string | null;
      mrrCents?: number;
      reason?: string;
      paymentGatewayConfigId?: string | null;
    },
  ) => api.patch<AdminTenantDetail>(`/admin/tenants/${id}/billing`, data),

  /** Re-sincroniza Tenant.max* (e flags) com os defaults atuais do
   *  plano (sem trocar de plano). Util quando operador editou o plano
   *  em /admin/plans e quer aplicar o novo limite aos tenants
   *  existentes sem forcar troca de plano. Auditado em AdminAuditLog. */
  syncTenantWithPlan: (id: string) =>
    api.post<{
      tenant: AdminTenantDetail;
      changed: boolean;
      plan: {
        code: string;
        maxBarbers: number;
        maxAppointmentsPerMonth: number;
        whatsappEnabled: boolean;
        chatbotAiEnabled: boolean;
      };
    }>(`/admin/tenants/${id}/sync-plan`),

  /** Confirma manualmente a renovacao (caso boleto): avanca o periodo e faz a
   *  tarja de renovacao do dono sumir. Pix some sozinho pelo webhook. */
  confirmTenantRenewal: (id: string) =>
    api.post<{ currentPeriodEnd: string; subscriptionStatus: string }>(
      `/admin/tenants/${id}/confirm-renewal`,
    ),

  deleteTenant: (id: string, data: { confirmSlug: string; reason?: string; force?: boolean }) =>
    api.delete<{ deleted: boolean; slug: string; removedCounts: Record<string, number>; archiveId?: string }>(
      `/admin/tenants/${id}`,
      { data },
    ),

  // U9-B - arquivos (dump/reimport) de tenant
  listTenantArchives: (id: string) =>
    api.get<TenantArchiveSummary[]>(`/admin/tenants/${id}/archives`),

  /**
   * Todos os arquivos da plataforma, inclusive os de tenant ja deletado - que
   * sao justamente os que importam na hora de restaurar.
   */
  listAllTenantArchives: () => api.get<TenantArchiveSummary[]>('/admin/archives'),

  // Backup do banco completo
  getDatabaseBackupPolicy: () =>
    api.get<DatabaseBackupPolicy>('/admin/database/backup/policy'),

  /** Baixa o dump como arquivo. Nada persiste no servidor - e a unica saida. */
  downloadDatabaseBackup: () =>
    api.get<Blob>('/admin/database/backup', { responseType: 'blob' }),

  createTenantArchive: (id: string) =>
    api.post<TenantArchiveSummary>(`/admin/tenants/${id}/archives`),

  reimportTenantArchive: (id: string, archiveId: string) =>
    api.post<TenantReimportResult>(`/admin/tenants/${id}/archives/${archiveId}/reimport`),

  /** Baixa o JSON do arquivo como blob (mantém a autenticação do client). */
  downloadTenantArchive: (id: string, archiveId: string) =>
    api.get<Blob>(`/admin/tenants/${id}/archives/${archiveId}/download`, {
      responseType: 'blob',
    }),

  resetOwnerPassword: (id: string) =>
    api.post<{
      ownerEmail: string;
      ownerName: string | null;
      newPassword: string;
      loginUrl: string;
    }>(`/admin/tenants/${id}/reset-owner-password`),

  listAudit: (params?: {
    action?: string;
    tenantId?: string;
    superAdminId?: string;
    page?: number;
    limit?: number;
  }) =>
    api.get<AdminListResponse<AdminAuditEntry>>('/admin/audit', {
      params,
    }),

  /** Preview - quantos AdminAuditLog seriam apagados num purge.
   *  olderThanDays=0 significa "tudo". Entries com action='audit_purge'
   *  sao preservados (forensics). */
  previewAuditCleanup: (olderThanDays: number) =>
    api.get<{
      total: number;
      olderThanDays: number;
      cutoff: string | null;
      oldestAffected: string | null;
      newestAffected: string | null;
    }>('/admin/audit/cleanup-preview', { params: { olderThanDays } }),

  /** Executa o purge. Body protege contra refresh acidental. Registra
   *  um novo AdminAuditLog com action=audit_purge + metadata da operacao. */
  runAuditCleanup: (olderThanDays: number) =>
    api.delete<{ deletedCount: number; olderThanDays: number }>(
      '/admin/audit',
      { data: { olderThanDays } },
    ),

  /**
   * Eliminação LGPD (direito ao esquecimento) de um titular: anonimiza os
   * cadastros em todos os tenants e deleta a identidade global. Irreversível.
   */
  eliminateEndUser: (endUserId: string, reason?: string) =>
    api.delete<{ eliminated: boolean; customersAnonymized: number; tenantsAffected: number }>(
      `/admin/identities/${endUserId}`,
      { data: { reason } },
    ),

  /** Limpa identidades fantasma (EndUser sem nenhum vínculo), pós-testes. */
  purgeTestIdentities: () =>
    api.post<{ purged: number }>('/admin/identities/purge-test', {}),

  startImpersonation: (
    tenantId: string,
    data: { reason?: string; targetUserId?: string },
  ) =>
    api.post<{
      access_token: string;
      session: {
        id: string;
        targetTenantId: string;
        targetUserId: string;
        startedAt: string;
      };
      tenant: { id: string; slug: string; name: string };
      target: {
        id: string;
        email: string;
        name: string | null;
        role: string;
      };
    }>(`/admin/tenants/${tenantId}/impersonate`, data),

  endImpersonation: () =>
    api.post<{
      access_token: string;
      session: { id: string; endedAt: string | null };
      user: {
        id: string;
        email: string;
        name: string | null;
        role: string;
        tenantId: string | null;
      };
    }>('/admin/impersonate/end'),

  recentSignups: (params?: { hours?: number; limit?: number }) =>
    api.get<{
      since: string;
      hours: number;
      total: number;
      items: Array<{
        id: string;
        slug: string;
        name: string;
        status: AdminTenantStatus;
        plan: AdminPlanType;
        createdAt: string;
        owner: {
          email: string;
          name: string | null;
          emailVerified: boolean;
        } | null;
      }>;
    }>('/admin/tenants/recent-signups', { params }),

  listPaymentGateways: () =>
    api.get<PaymentGatewayConfigRow[]>('/admin/payment-gateways'),

  getActivePaymentGateway: () =>
    api.get<{
      active: {
        source: 'vault';
        provider: string;
        mode: 'sandbox' | 'production';
        label: string | null;
        configId: string | null;
        hasWebhookToken: boolean;
      } | null;
    }>('/admin/payment-gateways/active'),

  createPaymentGateway: (data: {
    provider: string;
    mode: 'sandbox' | 'production';
    label: string;
    apiKey: string;
    webhookToken?: string | null;
    notes?: string | null;
    activate?: boolean;
  }) => api.post<PaymentGatewayConfigRow>('/admin/payment-gateways', data),

  updatePaymentGateway: (
    id: string,
    data: {
      label?: string;
      apiKey?: string;
      webhookToken?: string | null;
      notes?: string | null;
    },
  ) => api.patch<PaymentGatewayConfigRow>(`/admin/payment-gateways/${id}`, data),

  activatePaymentGateway: (id: string) =>
    api.post<PaymentGatewayConfigRow>(`/admin/payment-gateways/${id}/activate`),

  deactivatePaymentGateway: (id: string) =>
    api.post<PaymentGatewayConfigRow>(`/admin/payment-gateways/${id}/deactivate`),

  testPaymentGateway: (id: string) =>
    api.post<{ status: string; message: string }>(
      `/admin/payment-gateways/${id}/test`,
    ),

  deletePaymentGateway: (id: string) =>
    api.delete<{ ok: boolean }>(`/admin/payment-gateways/${id}`),

  // ─── Provedores de OTP (espelha payment-gateways) ───────────────────────────
  listOtpProviders: () =>
    api.get<OtpProviderConfigRow[]>('/admin/otp-providers'),

  createOtpProvider: (data: {
    providerType: string;
    mode: 'sandbox' | 'production';
    label: string;
    credential?: string | null;
    notes?: string | null;
    activate?: boolean;
  }) => api.post<OtpProviderConfigRow>('/admin/otp-providers', data),

  updateOtpProvider: (
    id: string,
    data: { label?: string; credential?: string | null; notes?: string | null },
  ) => api.patch<OtpProviderConfigRow>(`/admin/otp-providers/${id}`, data),

  activateOtpProvider: (id: string) =>
    api.post<OtpProviderConfigRow>(`/admin/otp-providers/${id}/activate`),

  deactivateOtpProvider: (id: string) =>
    api.post<OtpProviderConfigRow>(`/admin/otp-providers/${id}/deactivate`),

  testOtpProvider: (id: string) =>
    api.post<{ status: string; message: string }>(`/admin/otp-providers/${id}/test`),

  deleteOtpProvider: (id: string) =>
    api.delete<{ ok: boolean }>(`/admin/otp-providers/${id}`),

  updateTenantOtpProvider: (id: string, otpProviderConfigId: string | null) =>
    api.patch<AdminTenantDetail>(`/admin/tenants/${id}/otp-provider`, {
      otpProviderConfigId,
    }),

  getSystemSecretsStatus: () =>
    api.get<SystemSecretVaultStatus>('/admin/system/secrets/status'),

  listSystemSecrets: () =>
    api.get<SystemSecretRow[]>('/admin/system/secrets'),

  createSystemSecret: (data: {
    secretKey: string;
    label: string;
    category: string;
    provider?: string | null;
    purpose?: string | null;
    secretValue: string;
  }) => api.post<SystemSecretRow>('/admin/system/secrets', data),

  updateSystemSecret: (
    id: string,
    data: {
      label?: string;
      category?: string;
      provider?: string | null;
      purpose?: string | null;
      active?: boolean;
      status?: string;
      secretValue?: string;
    },
  ) => api.patch<SystemSecretRow>(`/admin/system/secrets/${id}`, data),

  revokeSystemSecret: (id: string) =>
    api.post<SystemSecretRow>(`/admin/system/secrets/${id}/revoke`),

  // ─── IA (chatbot) ───────────────────────────────────────────────
  // Configuracao do provedor de IA usado pelo chatbot WhatsApp.
  // Apenas SUPER_ADMIN cadastra; tenants ligam/desligam via toggle
  // chatbotAiEnabled em /admin/tenants/[id].
  listAiConfigs: () => api.get<AiConfigRow[]>('/admin/ai-configs'),

  /**
   * Retorna o template padrao da plataforma para persona do chatbot.
   * Usado pelo painel /admin/integrations/ai para pre-popular o
   * textarea na criacao e oferecer botao "Restaurar template padrao".
   */
  getDefaultAiPersona: () =>
    api.get<{ template: string; placeholders: string[] }>(
      '/admin/ai-configs/default-persona',
    ),

  getActiveAiConfig: () =>
    api.get<{
      active: {
        configId: string;
        provider: string;
        label: string;
        model: string;
        temperature: number;
        maxTokens: number;
        hasPersona: boolean;
      } | null;
    }>('/admin/ai-configs/active'),

  createAiConfig: (data: {
    provider: string;
    label: string;
    apiKey: string;
    model?: string;
    temperature?: number;
    maxTokens?: number;
    defaultPersona?: string | null;
    notes?: string | null;
    activate?: boolean;
  }) => api.post<AiConfigRow>('/admin/ai-configs', data),

  updateAiConfig: (
    id: string,
    data: {
      label?: string;
      apiKey?: string;
      model?: string;
      temperature?: number;
      maxTokens?: number;
      defaultPersona?: string | null;
      notes?: string | null;
    },
  ) => api.patch<AiConfigRow>(`/admin/ai-configs/${id}`, data),

  activateAiConfig: (id: string) =>
    api.post<AiConfigRow>(`/admin/ai-configs/${id}/activate`),

  deactivateAiConfig: (id: string) =>
    api.post<AiConfigRow>(`/admin/ai-configs/${id}/deactivate`),

  testAiConfig: (id: string) =>
    api.post<{ status: string; message: string }>(
      `/admin/ai-configs/${id}/test`,
    ),

  deleteAiConfig: (id: string) =>
    api.delete<{ ok: boolean }>(`/admin/ai-configs/${id}`),

  /**
   * Toggles per-tenant das integracoes.
   * Independente do plano - super-admin pode forcar um valor pra um
   * tenant especifico. Mudancas em /admin/tenants/[id]/billing resetam.
   */
  updateTenantIntegrations: (
    id: string,
    data: { whatsappEnabled?: boolean; chatbotAiEnabled?: boolean },
  ) =>
    api.patch<{ id: string; whatsappEnabled: boolean; chatbotAiEnabled: boolean }>(
      `/admin/tenants/${id}/integrations`,
      data,
    ),

  listObservability: () => api.get<ObservabilityConfigRow[]>('/admin/observability'),

  getActiveObservability: () =>
    api.get<{
      api: ObservabilityActiveSummary | null;
      web: ObservabilityActiveSummary | null;
    }>('/admin/observability/active'),

  createObservability: (data: {
    provider: string;
    target: 'api' | 'web';
    label: string;
    dsn: string;
    environment?: string;
    notes?: string | null;
    activate?: boolean;
  }) => api.post<ObservabilityConfigRow>('/admin/observability', data),

  updateObservability: (
    id: string,
    data: {
      label?: string;
      dsn?: string;
      environment?: string;
      notes?: string | null;
    },
  ) => api.patch<ObservabilityConfigRow>(`/admin/observability/${id}`, data),

  activateObservability: (id: string) =>
    api.post<ObservabilityConfigRow>(`/admin/observability/${id}/activate`),

  deactivateObservability: (id: string) =>
    api.post<ObservabilityConfigRow>(`/admin/observability/${id}/deactivate`),

  testObservability: (id: string) =>
    api.post<{ status: string; message: string }>(`/admin/observability/${id}/test`),

  deleteObservability: (id: string) =>
    api.delete<{ ok: boolean }>(`/admin/observability/${id}`),

  // ─── Notifications channel (email transacional) ────────────────────────────
  listNotifications: () =>
    api.get<NotificationChannelRow[]>('/admin/integrations/notifications'),

  getActiveNotification: () =>
    api.get<NotificationActiveSummary | null>(
      '/admin/integrations/notifications/active',
    ),

  createNotification: (data: {
    provider: NotificationProvider;
    label: string;
    apiKey?: string | null;
    fromAddress: string;
    replyTo?: string | null;
    supportEmail: string;
    smtpHost?: string | null;
    smtpPort?: number | null;
    smtpSecure?: boolean | null;
    smtpUser?: string | null;
    smtpPass?: string | null;
    notes?: string | null;
    activate?: boolean;
  }) => api.post<NotificationChannelRow>('/admin/integrations/notifications', data),

  updateNotification: (
    id: string,
    data: {
      label?: string;
      apiKey?: string;
      fromAddress?: string;
      replyTo?: string | null;
      supportEmail?: string;
      smtpHost?: string;
      smtpPort?: number;
      smtpSecure?: boolean;
      smtpUser?: string;
      smtpPass?: string;
      notes?: string | null;
    },
  ) =>
    api.patch<NotificationChannelRow>(
      `/admin/integrations/notifications/${id}`,
      data,
    ),

  activateNotification: (id: string) =>
    api.post<NotificationChannelRow>(
      `/admin/integrations/notifications/${id}/activate`,
    ),

  deactivateNotification: (id: string) =>
    api.post<NotificationChannelRow>(
      `/admin/integrations/notifications/${id}/deactivate`,
    ),

  testNotification: (id: string) =>
    api.post<{ status: string; message: string }>(
      `/admin/integrations/notifications/${id}/test`,
    ),

  deleteNotification: (id: string) =>
    api.delete<{ ok: boolean }>(`/admin/integrations/notifications/${id}`),

  // ─── WhatsApp resilience (super-admin) ─────────────────────────────────────
  listWhatsappTenants: () =>
    api.get<AdminWhatsappListResponse>('/admin/integrations/whatsapp/tenants'),

  reconnectWhatsappTenant: (tenantId: string) =>
    api.post<AdminWhatsappReconnectResponse>(
      `/admin/integrations/whatsapp/tenants/${tenantId}/reconnect`,
    ),

  resetWhatsappQr: (tenantId: string) =>
    api.post<AdminWhatsappResetQrResponse>(
      `/admin/integrations/whatsapp/tenants/${tenantId}/reset-qr`,
    ),

  // ─── Mensagens dinamicas (super-admin) ─────────────────────────────────────
  listMessages: (params?: {
    category?: string;
    surface?: PlatformMessageSurface;
    route?: string;
    tenantId?: string;
  }) =>
    api.get<AdminMessagesListResponse>('/admin/messages', {
      params: {
        ...(params?.category ? { category: params.category } : {}),
        ...(params?.surface ? { surface: params.surface } : {}),
        ...(params?.route ? { route: params.route } : {}),
        ...(params?.tenantId ? { tenantId: params.tenantId } : {}),
      },
    }),

  listMessageRoutes: () =>
    api.get<AdminMessagesRoutesResponse>('/admin/messages/routes'),

  updateMessage: (key: string, value: string, tenantId?: string) =>
    api.put<{ ok: boolean }>(
      `/admin/messages/${encodeURIComponent(key)}`,
      { value },
      { params: tenantId ? { tenantId } : {} },
    ),

  updateMessageRenderConfig: (
    key: string,
    config: {
      renderStyle?: PlatformMessageRenderStyle | null;
      position?: PlatformMessagePosition | null;
      durationMs?: number | null;
    },
    tenantId?: string,
  ) =>
    api.put<{ ok: boolean }>(
      `/admin/messages/${encodeURIComponent(key)}/render-config`,
      config,
      { params: tenantId ? { tenantId } : {} },
    ),

  resetMessage: (key: string, tenantId?: string) =>
    api.post<{ ok: boolean }>(
      `/admin/messages/${encodeURIComponent(key)}/reset`,
      {},
      { params: tenantId ? { tenantId } : {} },
    ),

  // ─── Saude do sistema (super-admin) ────────────────────────────────────────
  getSystemHealth: () =>
    api.get<AdminSystemHealthResponse>('/admin/system/health'),

  // ─── Monitoramento Uptime Kuma (super-admin) ──────────────────────────────
  getMonitoringSnapshot: () =>
    api.get<AdminMonitoringSnapshot>('/admin/integrations/monitoring/snapshot'),

  refreshMonitoringSnapshot: () =>
    api.post<AdminMonitoringSnapshot>('/admin/integrations/monitoring/refresh'),
};

export interface ObservabilityActiveSummary {
  source: 'vault';
  provider: string;
  label: string | null;
  configId: string | null;
  environment: string;
  dsnMasked: string;
}

export interface ObservabilityConfigRow {
  id: string;
  provider: string;
  target: 'api' | 'web';
  label: string;
  dsn?: string; // mascarada na listagem
  dsnMasked: string;
  dsnLength: number;
  secretStorage?: 'vault' | 'legacy_plaintext';
  secretVaultId?: string | null;
  environment: string;
  active: boolean;
  notes: string | null;
  lastTestStatus: string | null;
  lastTestMessage: string | null;
  lastTestedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export type NotificationProvider = 'resend' | 'smtp';

export interface NotificationActiveSummary {
  source: 'vault';
  provider: NotificationProvider;
  label: string | null;
  configId: string | null;
  fromAddress: string;
  replyTo: string | null;
  supportEmail: string;
  /** apiKey (resend) ou smtpPass (smtp) mascarado. */
  secretMasked: string;
  /** smtp.gmail.com etc - null quando provider=resend. */
  smtpHost: string | null;
}

export interface NotificationChannelRow {
  id: string;
  provider: NotificationProvider;
  label: string;
  /** Apenas quando provider=resend. */
  apiKeyMasked: string | null;
  apiKeyLength: number;
  fromAddress: string;
  replyTo: string | null;
  supportEmail: string;
  /** Apenas quando provider=smtp. */
  smtpHost: string | null;
  smtpPort: number | null;
  smtpSecure: boolean | null;
  smtpUser: string | null;
  smtpPassMasked: string | null;
  active: boolean;
  notes: string | null;
  lastTestStatus: string | null;
  lastTestMessage: string | null;
  lastTestedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface AiConfigRow {
  id: string;
  provider: string;
  label: string;
  apiKeyMasked: string;
  apiKeyLength: number;
  secretStorage: 'vault' | 'legacy_plaintext';
  secretVaultId: string | null;
  model: string;
  temperature: number;
  maxTokens: number;
  defaultPersona: string | null;
  active: boolean;
  notes: string | null;
  lastTestStatus: string | null;
  lastTestMessage: string | null;
  lastTestedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface SystemSecretVaultStatus {
  configured: boolean;
  keyVersion: string;
  source: 'env' | 'file' | 'missing';
}

export interface SystemSecretRow {
  id: string;
  secretKey: string;
  label: string;
  category: string;
  provider: string | null;
  purpose: string | null;
  algorithm: string;
  keyVersion: string;
  fingerprint: string | null;
  secretMasked: string;
  secretLength: number;
  active: boolean;
  status: string;
  createdBy: string | null;
  updatedBy: string | null;
  rotatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentGatewayConfigRow {
  id: string;
  provider: string;
  mode: 'sandbox' | 'production';
  label: string;
  apiKeyMasked: string;
  apiKeyLength: number;
  webhookToken: string | null;
  active: boolean;
  notes: string | null;
  lastTestStatus: string | null;
  lastTestMessage: string | null;
  lastTestedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface OtpProviderConfigRow {
  id: string;
  providerType: string;
  mode: 'sandbox' | 'production';
  label: string;
  active: boolean;
  notes: string | null;
  hasCredential: boolean;
  lastTestStatus: string | null;
  lastTestMessage: string | null;
  lastTestedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Billing (Asaas) ─────────────────────────────────────────────────────────

export interface BillingPlan {
  code: AdminPlanType;
  name: string;
  description: string | null;
  priceCents: number;
  maxBarbers: number;
  maxAppointmentsPerMonth: number;
  whatsappEnabled: boolean;
  chatbotAiEnabled: boolean;
  active?: boolean;
  publiclyListed: boolean;
  /** Destaque comercial ("Mais popular") na lista publica /pricing. */
  featured: boolean;
  /** Lista de features do card (/pricing), editavel no CRUD. Vazio = derivada. */
  features?: PlanFeature[];
  /** Ciclos longos (semestral/anual) habilitados pelo Super Admin. Vazio = so mensal. */
  prices?: PlanCyclePrice[];
}

export interface BillingSubscription {
  id: string;
  slug: string;
  /** Codigo do plano (ex: TESTE_COBRANCA, STARTER). Identificacao tecnica. */
  plan: AdminPlanType;
  /** Nome amigavel do plano (ex: "Teste de Cobrança"). Use no UI. */
  planName: string | null;
  status: AdminTenantStatus;
  subscriptionStatus: string | null;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  /** OVERRIDE explícito de mensalidade (0 = sem override). Use monthlyPriceCents no UI. */
  mrrCents: number;
  /** Mensalidade efetiva: preço vigente do plano, ou override quando definido. */
  monthlyPriceCents: number;
  asaasCustomerId: string | null;
  asaasSubscriptionId: string | null;
  asaasPaymentLink: string | null;
  trialDaysLeft: number | null;
  billingEnabled: boolean;
  env: 'sandbox' | 'production';
  /** Ciclo da assinatura ativa: 'MONTHLY' | 'SEMIANNUAL' | 'ANNUAL'. null = mensal/sem assinatura. */
  billingCycle: BillingCycle | null;
  /** Dias ate o fim do periodo atual (negativo se ja venceu). null sem assinatura ativa. */
  daysUntilRenewal: number | null;
  /** Janela (em dias) em que o lembrete de renovacao aparece. DB-driven. */
  renewalReminderDays: number;
  /** true quando o tenant deve ver a tarja de renovacao (proximo do vencimento ou past_due). */
  renewalDue: boolean;
  /**
   * Estado real da cobranca derivado no backend - separa o ciclo de vida
   * (Tenant.status) da existencia de assinatura Asaas. Define rotulo, tom e
   * quais campos o painel exibe.
   */
  subscriptionState: BillingSubscriptionState;
  /** Rotulo visivel do estado, resolvido no banco (PlatformMessages). */
  subscriptionStateLabel: string;
  /** true quando existe um ciclo de assinatura ativo/atrasado (Asaas OU manual). */
  hasSubscription: boolean;
  /** true = cobranca gerida pelo Asaas (gera Pix/boleto, permite "Pagar agora"). */
  managedByAsaas: boolean;
}

/** Estado de cobranca honesto, resolvido no backend (ver BillingService). */
export type BillingSubscriptionState =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'pending'
  | 'courtesy'
  | 'inactive';

export interface BillingPaymentInstructions {
  payment: {
    id: string;
    billingType: 'PIX' | 'BOLETO' | 'CREDIT_CARD' | 'UNDEFINED' | string | null;
    status: string;
    value: number;
    dueDate: string;
  } | null;
  pix: {
    encodedImage: string;
    payload: string;
    expirationDate: string;
  } | null;
  boleto: {
    identificationField: string | null;
    barCode: string | null;
    nossoNumero: string | null;
  } | null;
  message: string | null;
}

export const billingApi = {
  listPlans: () => api.get<BillingPlan[]>('/billing/plans'),

  getCurrent: () => api.get<BillingSubscription>('/billing/subscription'),

  subscribe: (data: {
    plan: AdminPlanType;
    cpfCnpj?: string;
    phone?: string;
    billingType?: 'PIX' | 'BOLETO';
    cycle?: BillingCycle;
  }) =>
    api.post<{
      subscriptionId: string;
      paymentId: string | null;
      billingType: 'PIX' | 'BOLETO';
      status: string;
      cycle: string;
      value: number;
    }>('/billing/subscribe', data),

  /** Cancela a subscription atual e cria nova com o plano escolhido. */
  changePlan: (data: {
    plan: AdminPlanType;
    cpfCnpj?: string;
    phone?: string;
    billingType?: 'PIX' | 'BOLETO';
    cycle?: BillingCycle;
  }) =>
    api.post<{
      subscriptionId: string;
      paymentId: string | null;
      billingType: 'PIX' | 'BOLETO';
      status: string;
      cycle: string;
      value: number;
    }>('/billing/change-plan', data),

  getPaymentInstructions: () =>
    api.get<BillingPaymentInstructions>('/billing/payment-instructions'),

  getPayments: () => api.get<BillingPaymentHistoryItem[]>('/billing/payments'),

  cancel: () => api.delete('/billing/subscription'),
};

/** Uma cobrança individual do tenant (Payment) - histórico + faturas a pagar. */
export interface BillingPaymentHistoryItem {
  id: string;
  asaasPaymentId: string;
  /** PENDING | RECEIVED | CONFIRMED | OVERDUE | REFUNDED | DELETED. */
  status: string;
  /** PIX | BOLETO | CREDIT_CARD | UNDEFINED | null. */
  billingType: string | null;
  valueCents: number;
  netValueCents: number | null;
  dueDate: string | null;
  paidAt: string | null;
  /** URL da fatura no provedor de pagamento (abre em nova aba). */
  invoiceUrl: string | null;
  createdAt: string;
}

// ─── Services ─────────────────────────────────────────────────────────────────

export interface Service {
  id: string;
  name: string;
  description?: string;
  duration: number;
  price: number;
  photo?: string | null;
  photoAssetId?: string | null;
  photoUrl?: string | null;
  imageUrl?: string;
  displayPhoto?: boolean;
  active: boolean;
  /** Público do serviço (Agenda Personalizada): 'M' | 'F' | 'UX' (Unissex). */
  audience?: ServiceAudience;
  /** Profissionais vinculados (Agenda Personalizada). Vazio = todos executam. */
  barberIds?: string[];
  tenantId: string;
  createdAt: string;
  updatedAt: string;
}

export const servicesApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string }) =>
    api.get<PaginatedResponse<Service>>('/services', { params }),

  /** Lista SERVER-SIDE (tabela do "Cartao completo"): so a pagina pedida volta.
   *  Filtros/ordenacao no banco. `getAll` (sem page) segue pros dropdowns. */
  getPaged: (params: {
    page: number;
    pageSize: number;
    search?: string;
    status?: 'ativo' | 'inativo';
    audience?: ServiceAudience;
    sortBy?: 'name' | 'duration' | 'price' | 'status';
    sortDir?: 'asc' | 'desc';
  }) => api.get<PagedResult<Service>>('/services', { params }),

  getOne: (id: string) => api.get<Service>(`/services/${id}`),

  create: (data: Partial<Service>) => api.post<Service>('/services', data),

  update: (id: string, data: Partial<Service>) => api.put<Service>(`/services/${id}`, data),

  delete: (id: string) => api.delete(`/services/${id}`),

  uploadImage: (id: string, file: File) => {
    const form = new FormData();
    form.append('photo', file);
    return api.post<Service>(`/services/${id}/photo`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// ─── Barbers ──────────────────────────────────────────────────────────────────

export interface Barber {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  bio?: string;
  photo?: string | null;
  photoAssetId?: string | null;
  photoUrl?: string | null;
  displayPhoto?: boolean;
  specialties: string[];
  active: boolean;
  /** Barbeiro titular da barbearia (derivado no backend). Não pode ser desativado/excluído. */
  isPrimary?: boolean;
  /** Serviços vinculados (Agenda Personalizada). Vazio = executa todos. */
  serviceIds?: string[];
  tenantId: string;
  createdAt: string;
  updatedAt: string;
}

/** Pre-flight da exclusão definitiva de um barbeiro (U12). */
export interface BarberPurgePreview {
  barberId: string;
  barberName: string;
  active: boolean;
  counts: {
    appointments: number;
    schedules: number;
    breaks: number;
    availabilityBlocks: number;
    daysOff: number;
    reassignmentOffers: number;
  };
  blocked: boolean;
  blockReasons: string[];
}

export const barbersApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string }) =>
    api.get<PaginatedResponse<Barber>>('/barbers', { params }),

  /** Lista SERVER-SIDE (tabela do "Cartao completo"): so a pagina pedida volta.
   *  Filtros/ordenacao no banco. `getAll` (sem page) segue pros dropdowns. */
  getPaged: (params: {
    page: number;
    pageSize: number;
    search?: string;
    status?: 'ativo' | 'inativo';
    specialties?: string[];
    sortBy?: 'name' | 'email' | 'status' | 'specialties';
    sortDir?: 'asc' | 'desc';
  }) =>
    api.get<PagedResult<Barber>>('/barbers', {
      params: {
        ...params,
        specialties: params.specialties?.length
          ? params.specialties.join(',')
          : undefined,
      },
    }),

  getOne: (id: string) => api.get<Barber>(`/barbers/${id}`),

  create: (data: Partial<Barber>) => api.post<Barber>('/barbers', data),

  update: (id: string, data: Partial<Barber>) => api.put<Barber>(`/barbers/${id}`, data),

  delete: (id: string) => api.delete(`/barbers/${id}`),

  /** Define o barbeiro como titular da barbearia (pré-req para desativar o titular atual). */
  setPrimary: (id: string) => api.post<Barber>(`/barbers/${id}/primary`),

  /** Pre-flight: o que será apagado se o barbeiro for excluído em definitivo. */
  purgePreview: (id: string) =>
    api.get<BarberPurgePreview>(`/barbers/${id}/purge-preview`),

  /** Exclusão definitiva em cascata (barbeiro inativo + sem agendamentos). */
  purge: (id: string, reason?: string) =>
    api.delete(`/barbers/${id}/purge`, {
      params: reason ? { reason } : undefined,
    }),

  uploadPhoto: (id: string, file: File) => {
    const form = new FormData();
    form.append('photo', file);
    return api.post<Barber>(`/barbers/${id}/photo`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  removePhoto: (id: string) => api.delete<Barber>(`/barbers/${id}/photo`),
};

// ─── Schedules ────────────────────────────────────────────────────────────────

export interface WeeklySchedule {
  barberId: string;
  schedule: {
    dayOfWeek: number;
    isOpen: boolean;
    openTime: string;
    closeTime: string;
  }[];
}

export interface Break {
  id: string;
  barberId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  label?: string;
}

export interface DayOff {
  id: string;
  barberId: string;
  date: string;
  reason?: string;
}

export type AvailabilityBlockScope = 'BARBERSHOP' | 'BARBER';
export type AvailabilityBlockStatus = 'ACTIVE' | 'CANCELLED';

export interface AvailabilityBlock {
  id: string;
  tenantId: string;
  barberId: string | null;
  barber?: { id: string; name: string } | null;
  scope: AvailabilityBlockScope;
  date: string;
  startTime: string;
  endTime: string;
  reason?: string | null;
  status: AvailabilityBlockStatus;
  createdByUserId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AvailabilityBlockImpactAppointment {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  customerName: string;
  customerPhone: string;
  serviceName: string;
  serviceDuration: number;
  servicePrice: number;
  barberName: string;
  action: 'CANCEL' | 'OFFER_REASSIGNMENT' | 'NONE';
  alternativeBarberId?: string | null;
  alternativeBarberName?: string | null;
}

export interface AvailabilityBlockImpact {
  total: number;
  cancelCount: number;
  offerCount: number;
  /** Impacto financeiro: soma dos preços dos agendamentos afetados. */
  revenueAtRisk: number;
  appointments: AvailabilityBlockImpactAppointment[];
}

type ScheduleSlotWire = {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isActive: boolean;
};

function wireToWeekly(barberId: string, slots: ScheduleSlotWire[]): WeeklySchedule {
  return {
    barberId,
    schedule: slots.map((s) => ({
      dayOfWeek: s.dayOfWeek,
      isOpen: s.isActive,
      openTime: s.startTime,
      closeTime: s.endTime,
    })),
  };
}

export const schedulesApi = {
  getAvailabilityBlocks: (params?: { from?: string; to?: string; barberId?: string }) =>
    api.get<AvailabilityBlock[]>('/schedules/unavailability', { params }),

  getAvailabilityBlockImpact: (params: {
    scope: AvailabilityBlockScope;
    barberId?: string;
    date: string;
    startTime: string;
    endTime: string;
  }) => api.get<AvailabilityBlockImpact>('/schedules/unavailability/impact', { params }),

  createAvailabilityBlock: (data: {
    scope: AvailabilityBlockScope;
    barberId?: string;
    date: string;
    startTime: string;
    endTime: string;
    reason?: string;
  }) =>
    api.post<{ block: AvailabilityBlock; impact: AvailabilityBlockImpact }>(
      '/schedules/unavailability',
      data,
    ),

  deleteAvailabilityBlock: (id: string) =>
    api.delete(`/schedules/unavailability/${id}`),

  getBarberSchedule: async (barberId: string) => {
    const res = await api.get<ScheduleSlotWire[]>(`/schedules/barber/${barberId}`);
    return { ...res, data: wireToWeekly(barberId, res.data ?? []) };
  },

  updateBarberSchedule: async (barberId: string, data: WeeklySchedule) => {
    const payload = {
      slots: data.schedule.map((s) => ({
        dayOfWeek: s.dayOfWeek,
        startTime: s.openTime,
        endTime: s.closeTime,
        isActive: s.isOpen,
      })),
    };
    const res = await api.put<ScheduleSlotWire[]>(`/schedules/barber/${barberId}`, payload);
    return { ...res, data: wireToWeekly(barberId, res.data ?? []) };
  },

  getBarberBreaks: (barberId: string) =>
    api.get<Break[]>(`/schedules/barber/${barberId}/breaks`),

  updateBarberBreaks: (barberId: string, data: Break[]) =>
    api.put<Break[]>(`/schedules/barber/${barberId}/breaks`, {
      breaks: data.map((b) => ({
        dayOfWeek: b.dayOfWeek,
        startTime: b.startTime,
        endTime: b.endTime,
      })),
    }),

  getDaysOff: async (barberId: string) => {
    const res = await api.get<DayOff[]>('/schedules/days-off');
    return { ...res, data: (res.data ?? []).filter((d) => d.barberId === barberId) };
  },

  createDayOff: (barberId: string, data: { date: string; reason?: string }) =>
    api.post<DayOff>('/schedules/days-off', { ...data, barberId }),

  deleteDayOff: (_barberId: string, dayOffId: string) =>
    api.delete(`/schedules/days-off/${dayOffId}`),
};

// ─── Appointments ─────────────────────────────────────────────────────────────

export type AppointmentStatus =
  | 'PENDING'
  | 'CONFIRMED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'NO_SHOW';

export type AppointmentFinancialStatus =
  | 'UNDECIDED'
  | 'PAID'
  | 'RECEIVABLE'
  | 'COURTESY';

export interface AppointmentFinancialDecision {
  status: Exclude<AppointmentFinancialStatus, 'UNDECIDED'>;
  amount?: number;
  notes?: string;
}

/**
 * Descritor de ação calculado pela fonte de verdade do backend
 * (AppointmentLifecycle.actionsFor). O terminal renderiza estes descritores;
 * não recalcula a matriz de transição. Ver
 * docs/02-domain/appointment-lifecycle/03-actions.md.
 */
export interface AppointmentLifecycleAction {
  key:
    | 'confirm'
    | 'start'
    | 'complete'
    | 'financial_adjust'
    | 'cancel'
    | 'cancel_record'
    | 'no_show'
    | 'reopen'
    | 'restore';
  labelKey: string;
  group: 'next_step' | 'financial' | 'exceptions' | 'correction';
  to: AppointmentStatus;
  financial?: AppointmentFinancialStatus;
  tone: 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'danger' | 'neutral';
}

export interface Appointment {
  id: string;
  tenantId: string;
  barberId: string;
  barber: Barber;
  serviceId: string;
  service: Service;
  customerId?: string;
  customer?: Customer;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  scheduledAt: string;
  endsAt: string;
  status: AppointmentStatus;
  financialStatus?: AppointmentFinancialStatus;
  financialAmount?: number | null;
  financialDecidedAt?: string | null;
  financialNotes?: string | null;
  requiresReview?: boolean;
  reviewReason?: 'OPERATION_DECISION_PENDING' | 'FINANCIAL_DECISION_PENDING' | null;
  /** Vencido (horário de término já passou) - calculado pelo backend no fuso BR. */
  isOverdue?: boolean;
  /** Bucket de exibição financeiro calculado pelo backend (ver 01-status.md). */
  financialBucket?: string;
  /** Ações disponíveis (matriz única do backend, por papel/terminal). */
  actions?: AppointmentLifecycleAction[];
  notes?: string;
  startedAt?: string;
  completedAt?: string;
  /** "Cliente de porta": atendimento sem agenda previa, lancado manualmente. */
  walkIn?: boolean;
  totalPrice: number;
  createdAt: string;
  updatedAt: string;
}

export interface AppointmentWeekDay {
  date: string;
  weekday: string;
  total: number;
  pending: number;
  confirmed: number;
  inProgress: number;
  completed: number;
  cancelled: number;
  noShow: number;
  appointments: Appointment[];
}

export interface AppointmentWeekSummary {
  weekStart: string;
  weekEnd: string;
  weekOffset: number;
  today: string;
  totals: {
    total: number;
    completed: number;
    cancelledOrNoShow: number;
    upcoming: number;
  };
  days: AppointmentWeekDay[];
}

export interface AppointmentMetrics {
  today: {
    total: number;
    projectedRevenue: number;
    completedRevenue: number;
    reviewPending: number;
    scheduledCount: number;
    pending: number;
    confirmed: number;
    inProgress: number;
    completed: number;
    cancelled: number;
    noShow: number;
    financialDecisionPending: number;
    nextHour: number;
    pendingAppointments: Appointment[];
    confirmedAppointments: Appointment[];
    inProgressAppointments: Appointment[];
    nextHourAppointments: Appointment[];
    cancelledAppointments: Appointment[];
    noShowAppointments: Appointment[];
    reviewAppointments: Appointment[];
  };
  attention: {
    financialDecisionPending: number;
    operationVencida: number;
    emCursoOuFuturo: number;
    noShowReview: number;
    resolved: number;
    total: number;
  };
  upcoming: Appointment[];
}

function normalizeAppointmentResponse<T>(response: { data: T }): { data: T } {
  const d = response.data as unknown;
  if (d && typeof d === 'object') {
    const obj = d as Record<string, unknown>;
    // Normalize paginated list
    if (Array.isArray(obj.items)) {
      obj.items = obj.items.map((item: unknown) => normalizeAppointment(item as Record<string, unknown>));
    }
    if (Array.isArray(obj.data)) {
      obj.data = (obj.data as unknown[]).map((item: unknown) => normalizeAppointment(item as Record<string, unknown>));
    }
    // Normalize upcoming in metrics
    if (Array.isArray(obj.upcoming)) {
      obj.upcoming = obj.upcoming.map((item: unknown) => normalizeAppointment(item as Record<string, unknown>));
    }
    if (obj.today && typeof obj.today === 'object') {
      const today = obj.today as Record<string, unknown>;
      if (Array.isArray(today.pendingAppointments)) {
        today.pendingAppointments = today.pendingAppointments.map((item: unknown) =>
          normalizeAppointment(item as Record<string, unknown>),
        );
      }
      if (Array.isArray(today.confirmedAppointments)) {
        today.confirmedAppointments = today.confirmedAppointments.map((item: unknown) =>
          normalizeAppointment(item as Record<string, unknown>),
        );
      }
      if (Array.isArray(today.inProgressAppointments)) {
        today.inProgressAppointments = today.inProgressAppointments.map((item: unknown) =>
          normalizeAppointment(item as Record<string, unknown>),
        );
      }
      if (Array.isArray(today.nextHourAppointments)) {
        today.nextHourAppointments = today.nextHourAppointments.map((item: unknown) =>
          normalizeAppointment(item as Record<string, unknown>),
        );
      }
      if (Array.isArray(today.cancelledAppointments)) {
        today.cancelledAppointments = today.cancelledAppointments.map((item: unknown) =>
          normalizeAppointment(item as Record<string, unknown>),
        );
      }
      if (Array.isArray(today.noShowAppointments)) {
        today.noShowAppointments = today.noShowAppointments.map((item: unknown) =>
          normalizeAppointment(item as Record<string, unknown>),
        );
      }
      if (Array.isArray(today.reviewAppointments)) {
        today.reviewAppointments = today.reviewAppointments.map((item: unknown) =>
          normalizeAppointment(item as Record<string, unknown>),
        );
      }
    }
    // Normalize single appointment
    if (obj.id && obj.barberId) {
      response.data = normalizeAppointment(obj) as T;
    }
  }
  return response;
}

/** Agregado por status do "Resumo da agenda" (count + valor = soma de service.price). */
export type AppointmentSummaryByStatus = Record<
  string,
  { count: number; value: number }
>;

/** Resposta paginada de /appointments + agregado do Resumo (aditivo). */
export interface AppointmentsListResponse extends PaginatedResponse<Appointment> {
  summaryByStatus?: AppointmentSummaryByStatus;
  summaryTotal?: number;
}

export const appointmentsApi = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    date?: string;
    dateFrom?: string;
    dateTo?: string;
    barberId?: string;
    status?: AppointmentStatus | 'OPEN';
    customer?: string;
    phone?: string;
    /** Recorte semantico dos cards do /dashboard (filtrado no backend -
     *  mesmas regras do getMetrics). Ex.: review-financial, needs-attention. */
    scope?: string;
    sortBy?: string;
    sortDir?: string;
    search?: string;
  }) => api.get<AppointmentsListResponse>('/appointments', { params }).then(normalizeAppointmentResponse),

  getOne: (id: string) => api.get<Appointment>(`/appointments/${id}`).then(normalizeAppointmentResponse),

  create: (data: {
    customerId: string;
    barberId: string;
    serviceId: string;
    date: string;
    startTime: string;
    endTime?: string;
    walkIn?: boolean;
    notes?: string;
  }) => api.post<Appointment>('/appointments', data),

  update: (
    id: string,
    data: {
      customerId?: string;
      barberId?: string;
      serviceId?: string;
      date?: string;
      startTime?: string;
      endTime?: string;
      walkIn?: boolean;
      notes?: string;
    },
  ) => api.put<Appointment>(`/appointments/${id}`, data),

  getAvailability: (params: {
    serviceId: string;
    date: string;
    barberId?: string;
    /** Exclui um agendamento do cálculo (edição: não conta como ocupando o
     *  próprio horário). */
    excludeAppointmentId?: string;
  }) => api.get<AvailabilitySlot[]>('/appointments/availability', { params }),

  updateStatus: (
    id: string,
    status: AppointmentStatus,
    financialDecision?: AppointmentFinancialDecision,
  ) =>
    api
      .put<Appointment>(`/appointments/${id}/status`, { status, financialDecision })
      .then(normalizeAppointmentResponse),

  getMetrics: () => api.get<AppointmentMetrics>('/appointments/metrics').then(normalizeAppointmentResponse),

  /**
   * Retorna a distribuicao dos agendamentos da semana corrente
   * (segunda a domingo) por dia, com contagens por status. Usado pelo
   * card "Agendamentos da semana" em /dashboard.
   */
  getWeek: (weekOffset = 0) =>
    api
      .get<AppointmentWeekSummary>('/appointments/week', {
        params: { weekOffset },
      })
      .then((r) => {
      // Normaliza os Appointment dentro de days[].appointments
      const days = r.data?.days ?? [];
      for (const d of days) {
        if (Array.isArray(d.appointments)) {
          d.appointments = d.appointments.map(
            (a) =>
              normalizeAppointment(
                a as unknown as Record<string, unknown>,
              ) as unknown as Appointment,
          );
        }
      }
      return r;
    }),
};

// ─── Customers ────────────────────────────────────────────────────────────────

export interface Customer {
  id: string;
  tenantId: string;
  name: string;
  /** Nullable: "cliente de porta" (walk-in) pode ser criado sem telefone. */
  phone: string | null;
  email?: string;
  birthDate?: string | null;
  notes?: string | null;
  marketingOptIn?: boolean;
  /** Gate por barbearia da aba "Historico" no app do cliente. Default false. */
  showHistoryInApp?: boolean;
  contactPending?: boolean;
  /** Foto de perfil global do cliente (EndUser.avatarUrl). Null = sem foto. */
  avatarUrl?: string | null;
  appointmentCount: number;
  lastVisit?: string | null;
  totalSpent: number;
  createdAt: string;
  /**
   * Segmento decidido no BACKEND (SSOT) - o badge so renderiza, nunca recalcula.
   * Os 6 valores do classify(): vip/fiel/ativo/em_risco/novo/inativo.
   */
  segment?:
    | 'vip'
    | 'fiel'
    | 'ativo'
    | 'em_risco'
    | 'novo'
    | 'inativo';
}

export interface CustomerProfilePayload {
  name: string;
  phone: string;
  email?: string | null;
  birthDate?: string | null;
  notes?: string | null;
  marketingOptIn?: boolean;
  /** Libera a aba "Historico" no app do cliente para este cliente (por barbearia). */
  showHistoryInApp?: boolean;
  /** Walk-in sem telefone: marca a ficha como contato pendente (só tem efeito
   * quando o telefone não foi informado). Completar depois limpa a flag. */
  contactPending?: boolean;
}

export interface CustomerStats {
  completedCount: number;
  cancelledCount: number;
  noShowCount: number;
  totalAppointments: number;
  totalSpent: number;
  averageTicket: number;
  averageDaysBetweenVisits: number | null;
  firstVisit: string | null;
  lastVisit: string | null;
  favoriteService: { id: string; name: string; count: number } | null;
  favoriteBarber: { id: string; name: string; count: number } | null;
}

export type CustomerDetail = Customer & {
  appointments: Appointment[];
  stats: CustomerStats;
};

export type CustomerSort =
  | 'name_asc'
  | 'name_desc'
  | 'newest'
  | 'most_spent'
  | 'least_spent'
  | 'most_visits'
  | 'least_visits'
  | 'recent_visit'
  | 'inactive';

export type CustomerSegment =
  | 'all'
  | 'new'
  | 'active'
  | 'returning'
  | 'at_risk'
  | 'reliable'
  | 'inactive'
  | 'one_visit'
  | 'two_visits'
  | 'three_visits'
  | 'four_plus_visits';

export interface CustomerRankingEntry {
  id: string;
  name: string;
  phone: string;
  completedCount: number;
  totalSpent: number;
  lastVisit: string | null;
  reliability: number | null;
  averageDaysBetweenVisits: number | null;
  cancelledCount: number;
  noShowCount: number;
}

export interface CustomerInsights {
  totals: {
    totalCustomers: number;
    newCustomers30d: number;
    activeCustomers90d: number;
    returningCustomers: number;
    atRiskCount: number;
    inactiveCount: number;
    returningRate: number;
    avgTicket: number;
    totalRevenue: number;
  };
  retentionBuckets: {
    one: number;
    two: number;
    three: number;
    fourPlus: number;
  };
  rankings: {
    topBySpend: CustomerRankingEntry[];
    topByVisits: CustomerRankingEntry[];
    mostReliable: CustomerRankingEntry[];
    atRisk: CustomerRankingEntry[];
  };
}

export type BirthdayStatus =
  | 'vip'
  | 'fiel'
  | 'ativo'
  | 'em_risco'
  | 'novo'
  | 'inativo';

export interface BirthdayCustomer {
  id: string;
  name: string;
  phone: string;
  email?: string | null;
  birthDate: string;
  birthDay: number;
  completedCount: number;
  cancelledCount: number;
  noShowCount: number;
  totalSpent: number;
  lastVisit: string | null;
  status: BirthdayStatus;
  /** Confiabilidade (fracao 0-1, null sem finalizados) - calculada no backend. */
  reliability: number | null;
  /** Ticket medio em BRL (0 sem visita) - calculado no backend. */
  avgTicket: number;
}

export interface BirthdaysResponse {
  month: number;
  items: BirthdayCustomer[];
}

export const customersApi = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    phone?: string;
    sort?: CustomerSort;
    segment?: CustomerSegment;
    from?: string;
    to?: string;
  }) => api.get<PaginatedResponse<Customer>>('/customers', { params }),

  getInsights: () => api.get<CustomerInsights>('/customers/insights'),

  getBirthdays: (month?: number) =>
    api.get<BirthdaysResponse>('/customers/birthdays', {
      params: month != null ? { month } : undefined,
    }),

  getOne: async (id: string) => {
    const res = await api.get<CustomerDetail>(`/customers/${id}`);
    if (res.data && Array.isArray(res.data.appointments)) {
      res.data = {
        ...res.data,
        appointments: res.data.appointments.map(
          (appt) => normalizeAppointment(appt as unknown as Record<string, unknown>) as unknown as Appointment,
        ),
      };
    }
    return res;
  },

  create: (data: CustomerProfilePayload) =>
    api.post<Customer>('/customers', data),

  update: (id: string, data: Partial<CustomerProfilePayload>) =>
    api.put<Customer>(`/customers/${id}`, data),
};

// ─── Quotas ───────────────────────────────────────────────────────────────────

export type QuotaLevel = 'ok' | 'warning' | 'critical' | 'exceeded';

export interface QuotaUsage {
  used: number;
  max: number;
  exceeded: boolean;
  remaining: number;
  /** Uso em % (0-100, arredondado). Vem do backend. */
  percent: number;
  /** Nivel de alerta DB-driven (thresholds em SystemRuntimeConfig). */
  level: QuotaLevel;
}

export interface MeQuotas {
  /** Barbeiros ativos vs maxBarbers do plano/tenant. */
  barber: QuotaUsage;
  /** Agendamentos do mes corrente vs maxAppointmentsPerMonth. */
  appointmentMonthly: QuotaUsage;
  /** Defaults atuais do plano que o tenant assina. Quando divergem de
   *  `barber.max`/`appointmentMonthly.max` (snapshot), o operador
   *  editou o plano em /admin/plans apos a assinatura. UI usa isso
   *  para mostrar transparencia ao cliente final. */
  plan: {
    code: string | null;
    maxBarbers: number | null;
    maxAppointmentsPerMonth: number | null;
  };
}

export interface BarberBreakdown {
  /** Ativos - consomem quota. */
  active: number;
  /** Inativos - cadastrados mas desabilitados; nao consomem quota. */
  inactive: number;
  /** Total = active + inactive. */
  total: number;
  max: number;
  exceeded: boolean;
  remaining: number;
}

export const quotasApi = {
  /** Quotas resolvidas do tenant logado (uso atual + limite + flag exceeded). */
  getMine: () => api.get<MeQuotas>('/quotas/me'),
  /** Detalhamento de barbeiros - ativos vs inativos vs total. Resolve
   *  a divergencia visual entre /admin/tenants (que mostrava total) e
   *  /dashboard/billing (que mostra ativos). UI exibe ambos com
   *  labels claras. */
  getBarberBreakdown: () =>
    api.get<BarberBreakdown>('/quotas/me/barber-breakdown'),
};

// ─── Capabilities ─────────────────────────────────────────────────────────────

export interface CapabilityRow {
  key: string;
  group: string;
  label: string;
  description: string | null;
  defaultRoles: string[];
  sortOrder: number;
  active: boolean;
}

export type TenantEntitlementState = 'GRANTED' | 'REVOKED';

/**
 * Resultado semantico do PUT /admin/capabilities/plans/:code. Permite
 * ao painel de log lateral renderizar exatamente o diff aplicado pelo
 * save - quais capabilities foram adicionadas/removidas/mantidas e
 * quais keys vieram invalidas (informativo - sao ignoradas pelo save).
 */
export interface SetPlanResult {
  planCode: string;
  added: string[];
  removed: string[];
  unchanged: string[];
  invalid: string[];
  total: number;
}

/** Resultado semantico do PUT /admin/capabilities/tenants/:tenantId. */
export interface SetTenantResult {
  tenantId: string;
  tenantSlug: string;
  planCode: string;
  granted: string[];
  revoked: string[];
  inherited: string[];
  unchanged: string[];
  invalid: string[];
  total: number;
}

export interface TenantEntitlementRow {
  capabilityKey: string;
  state: TenantEntitlementState;
  reason: string | null;
  grantedBy: string | null;
  grantedAt: string;
  expiresAt: string | null;
  capability: CapabilityRow;
}

export const capabilitiesApi = {
  /** Lista as capabilities resolvidas do user logado (consumido pelo
   *  hook `useCapabilities` no frontend para filtrar menu, gate de
   *  pagina e habilitar/desabilitar botoes). */
  getMine: () => api.get<{ capabilities: string[] }>('/capabilities/me'),

  /** Catalogo completo (super-admin). */
  listAll: () => api.get<{ items: CapabilityRow[] }>('/admin/capabilities'),

  /** Bundle do plano. */
  listForPlan: (code: string) =>
    api.get<{ items: CapabilityRow[] }>(`/admin/capabilities/plans/${code}`),

  /** Substitui o bundle do plano (super-admin). */
  setForPlan: (code: string, capabilityKeys: string[]) =>
    api.put<SetPlanResult>(
      `/admin/capabilities/plans/${code}`,
      { capabilityKeys },
    ),

  /** Lista overrides per-tenant (super-admin). */
  listForTenant: (tenantId: string) =>
    api.get<{ items: TenantEntitlementRow[] }>(
      `/admin/capabilities/tenants/${tenantId}`,
    ),

  /** Substitui os overrides do tenant. */
  setForTenant: (
    tenantId: string,
    overrides: Array<{
      capabilityKey: string;
      state: TenantEntitlementState;
      reason?: string | null;
      expiresAt?: string | null;
    }>,
  ) =>
    api.put<SetTenantResult>(
      `/admin/capabilities/tenants/${tenantId}`,
      { overrides },
    ),

  /** Forca re-execucao do seeder de capabilities. Idempotente -
   *  upserta o catalogo, preenche bundles iniciais para planos sem
   *  customizacao, e migra flags legadas. Usado quando o seeder de
   *  boot falhou silenciosamente em algum deploy. */
  reseed: () =>
    api.post<{
      ok: boolean;
      capabilities: number;
      planCapabilities: number;
      tenantEntitlements: number;
    }>('/admin/capabilities/reseed'),
};

// ─── Data Dictionary ─────────────────────────────────────────────────────────

export type DataDictionaryScope = 'table' | 'column' | 'domain';

export interface DataDictionaryDomain {
  identifier: string;
  purpose: string | null;
  notes: string | null;
}

export interface DataDictionaryColumn {
  name: string;
  dataType: string;
  isNullable: boolean;
  defaultValue: string | null;
  isPrimaryKey: boolean;
  isUnique: boolean;
  characterMaximumLength: number | null;
  purpose: string | null;
  notes: string | null;
  needsDoc: boolean;
}

export interface DataDictionaryIndex {
  name: string;
  columns: string[];
  isUnique: boolean;
  isPrimary: boolean;
}

export interface DataDictionaryForeignKey {
  name: string;
  columns: string[];
  referencedTable: string;
  referencedColumns: string[];
  onDelete: string;
  onUpdate: string;
}

export interface DataDictionaryTable {
  name: string;
  domain: string | null;
  purpose: string | null;
  notes: string | null;
  ownerArea: string | null;
  needsDoc: boolean;
  columns: DataDictionaryColumn[];
  indexes: DataDictionaryIndex[];
  foreignKeys: DataDictionaryForeignKey[];
  approxRowCount: number;
  coverage: number;
}

export interface DataDictionaryCatalog {
  domains: DataDictionaryDomain[];
  tables: DataDictionaryTable[];
  orphans: Array<{ identifier: string; domain: string | null }>;
  summary: {
    tableTotal: number;
    tableDocumented: number;
    tableCoveragePercent: number;
    columnTotal: number;
    columnDocumented: number;
    columnCoveragePercent: number;
  };
}

export interface DataDictionaryEntryRow {
  id: string;
  scope: DataDictionaryScope;
  identifier: string;
  domain: string | null;
  purpose: string | null;
  notes: string | null;
  ownerArea: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export const dataDictionaryApi = {
  /** Catalogo merged (introspect LIVE do PG + editorial em DB). */
  getCatalog: () => api.get<DataDictionaryCatalog>('/admin/data-dictionary'),

  /** Resumo de cobertura - alimenta CI smoke check. */
  getCoverage: () =>
    api.get<{
      ok: boolean;
      tablesMissingDoc: string[];
      summary: DataDictionaryCatalog['summary'];
    }>('/admin/data-dictionary/coverage'),

  /** Upsert do editorial (purpose/notes/dominio) de uma entry. */
  upsertEntry: (input: {
    scope: DataDictionaryScope;
    identifier: string;
    domain?: string | null;
    purpose?: string | null;
    notes?: string | null;
    ownerArea?: string | null;
  }) => api.put<DataDictionaryEntryRow>('/admin/data-dictionary/entry', input),

  /** Apaga uma entry (usado para limpar orphans - tabelas removidas). */
  deleteEntry: (scope: DataDictionaryScope, identifier: string) =>
    api.delete<{ ok: boolean }>(
      `/admin/data-dictionary/entry/${scope}/${encodeURIComponent(identifier)}`,
    ),

  /** Re-importa proposito de tabelas/colunas a partir dos JSDoc do
   *  schema.prisma. Respeita edicoes manuais (so atualiza entries
   *  marcadas `updatedBy='schema-import'`). Idempotente. */
  reseed: () =>
    api.post<{
      parsedModels: number;
      tableEntriesUpserted: number;
      columnEntriesUpserted: number;
      skippedDueToManualEdit: number;
    }>('/admin/data-dictionary/reseed'),
};

// ─── Loyalty ──────────────────────────────────────────────────────────────────

export type LoyaltyEarningRule = 'per_visit' | 'per_real';
export type LoyaltyTransactionType = 'EARN' | 'REDEEM' | 'ADJUST';

export interface LoyaltyReward {
  id: string;
  name: string;
  pointsCost: number;
  active: boolean;
}

export interface LoyaltyProgram {
  id: string;
  name: string;
  earningRule: LoyaltyEarningRule;
  pointsPerVisit: number;
  pointsPerReal: number;
  rewards: LoyaltyReward[];
}

export interface LoyaltyConfig {
  enabled: boolean;
  activeProgramId: string | null;
  earningRule: LoyaltyEarningRule;
  pointsPerVisit: number;
  pointsPerReal: number;
  rewards: LoyaltyReward[];
  programs: LoyaltyProgram[];
  /**
   * True quando o programa ainda não tem nenhuma transação (é fresh).
   * Usado pelo frontend para expor o botão de retroatividade só no
   * bootstrap, evitando clique acidental meses depois. Campo read-only.
   */
  canRetroact?: boolean;
}

export interface LoyaltyTransactionEntry {
  id: string;
  type: LoyaltyTransactionType;
  points: number;
  description: string | null;
  appointmentId: string | null;
  rewardId: string | null;
  createdAt: string;
}

export interface CustomerLoyalty {
  balance: number;
  enabled: boolean;
  activeProgramName?: string;
  transactions: LoyaltyTransactionEntry[];
  rewardsCatalog: LoyaltyReward[];
  availableRewards: LoyaltyReward[];
}

export interface LoyaltyRedeemableCustomer {
  id: string;
  name: string;
  phone?: string | null;
  balance: number;
  bestRewardName: string;
  bestRewardCost: number;
}

export interface LoyaltyNearRewardCustomer {
  id: string;
  name: string;
  phone?: string | null;
  balance: number;
  nextRewardName: string;
  nextRewardCost: number;
  missingPoints: number;
}

export interface LoyaltyInsights {
  enabled: boolean;
  customersWithRedeemableReward: number;
  customersNearReward: number;
  totalPointsInCirculation: number;
  totalRedemptions: number;
  readyToRedeem: LoyaltyRedeemableCustomer[];
  topRedeemable: LoyaltyRedeemableCustomer[];
  nearReward: LoyaltyNearRewardCustomer[];
}

export interface LoyaltyAuditReport {
  dryRun: true;
  generatedAt: string;
  config: {
    enabled: boolean;
    activeProgramId: string | null;
    activeProgramName: string;
    earningRule: LoyaltyEarningRule;
    pointsPerVisit: number;
    pointsPerReal: number;
    activeRewards: number;
    totalRewards: number;
  };
  summary: {
    completedAppointments: number;
    totalEarnTransactions: number;
    missingEarns: number;
    duplicateEarnGroups: number;
    positiveBalanceCustomers: number;
    readyToRedeemCustomers: number;
    nearRewardCustomers: number;
    totalRedemptions: number;
  };
  missingEarns: Array<{
    appointmentId: string;
    customerId: string;
    customerName: string;
    customerPhone: string | null;
    scheduledAt: string;
    completedAt: string | null;
    serviceName: string;
    servicePrice: number;
    expectedPoints: number;
  }>;
  duplicateEarns: Array<{
    appointmentId: string;
    customerId: string;
    customerName: string;
    customerPhone: string | null;
    scheduledAt: string;
    serviceName: string;
    transactionCount: number;
    transactionIds: string[];
    points: number[];
  }>;
  positiveBalances: Array<{
    customerId: string;
    name: string;
    phone: string | null;
    balance: number;
  }>;
  readyToRedeem: LoyaltyRedeemableCustomer[];
  nearReward: LoyaltyNearRewardCustomer[];
}

export const loyaltyApi = {
  getConfig: () => api.get<LoyaltyConfig>('/loyalty/config'),

  updateConfig: (data: Partial<LoyaltyConfig>) =>
    api.put<LoyaltyConfig>('/loyalty/config', data),

  getInsights: () => api.get<LoyaltyInsights>('/loyalty/insights'),

  audit: () => api.get<LoyaltyAuditReport>('/loyalty/audit'),

  getCustomerLoyalty: (customerId: string) =>
    api.get<CustomerLoyalty>(`/loyalty/customers/${customerId}`),

  redeem: (customerId: string, rewardId: string) =>
    api.post<LoyaltyTransactionEntry>(`/loyalty/customers/${customerId}/redeem`, {
      rewardId,
    }),

  adjust: (customerId: string, points: number, description?: string) =>
    api.post<LoyaltyTransactionEntry>(
      `/loyalty/customers/${customerId}/adjust`,
      { points, description },
    ),
};

// ─── Public API (booking widget) ──────────────────────────────────────────────

export interface TenantThemeSettings {
  brand?: string;
  brandLight?: string;
  brandDark?: string;
  brandHover?: string;
  brandOn?: string;
  surfaceGround?: string;
  surfaceDefault?: string;
  surfaceRaised?: string;
  surfaceOverlay?: string;
  borderDefault?: string;
  borderBrand?: string;
  textPrimary?: string;
  textSecondary?: string;
  textMuted?: string;
  textLink?: string;
  font?: string;
}

export type BookingStyle = 'ios' | 'android';

/**
 * Experiência do agendamento público /booking/<slug>: 'classic' (wizard atual,
 * fallback seguro) ou 'app' (estilo mobile, timeline + sheets). Escolha do dono
 * em /dashboard/settings; resolvida no backend com default global e fallback.
 */
export type BookingExperience = 'classic' | 'app';

/** Público do serviço (Agenda Personalizada): Masculino, Feminino, Unissex. */
export type ServiceAudience = 'M' | 'F' | 'UX';

/**
 * Agenda Personalizada (Tenant.settings.customSchedule). enabled=false
 * (default) = comportamento baseline exato (docs/02-domain/booking-baseline.md).
 * Sub-preferências independentes, alternáveis a qualquer momento pelo dono.
 */
export interface CustomScheduleSettings {
  enabled: boolean;
  flowOrder: 'service-first' | 'professional-first';
  audienceFilter: boolean;
  serviceOrdering: 'name' | 'audience';
}

export interface TenantSettings {
  theme?: TenantThemeSettings;
  /** Passo (min) de exibicao dentro de cada janela livre (motor de agenda dinamica). */
  slotGranularity?: number;
  /** Folga simetrica (min) entre servicos. 0 = servicos podem encostar. */
  bufferBetweenServices?: number;
  /**
   * Tema visual da rota publica /booking/<slug>. iOS adota cantos
   * mais arredondados, sombras suaves e tipografia stack Apple.
   * Android adota cantos mais retos, cores solidas e tipografia
   * stack Roboto. Default 'ios'.
   */
  bookingStyle?: BookingStyle;
  /**
   * Experiência do agendamento público: 'classic' (atual) | 'app' (estilo
   * mobile). Resolvida no backend (settings ?? default global ?? 'classic').
   */
  bookingExperience?: BookingExperience;
  /** Tema da plataforma para rotas de auth do tenant. */
  loginTheme?: string | null;
  /** Tema efetivo aplicado quando loginTheme = 7cors-system. */
  loginThemeEffective?: '7cors-light' | '7cors-dark' | string | null;
  /**
   * Quando true, a entrada do /booking/<slug> exibe duas opções:
   * "Fazer agendamento" e "Falar com atendente". O segundo botão
   * abre wa.me/<attendantPhone>.
   */
  attendantEnabled?: boolean;
  /** Telefone WhatsApp do atendente (E.164 ou DDI+DDD+numero). */
  attendantPhone?: string;
  /** Endereço estruturado da barbearia (fonte de edição; a coluna Tenant.address
   *  é a linha composta a partir destes campos). */
  address?: {
    zip?: string;
    street?: string;
    number?: string;
    complement?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
  };
  /** Telefone fixo de contato (exibição). Diferente de Tenant.phone, que é o
   *  celular/WhatsApp (vira instância Evolution). */
  landline?: string;
  /** Política de agendamento - janela de cancelamento/remarcação do cliente. */
  bookingPolicy?: {
    /** Legado (horas). Prefira cancellationMinutes; o backend converte (x60). */
    cancellationHours?: number;
    /** Prazo, em MINUTOS, para o cliente cancelar/remarcar pelo app. Canônico. */
    cancellationMinutes?: number;
  };
  /** Janela de agendamento: até quantos dias à frente (incluindo hoje) o cliente
   *  pode escolher data no app. Default/teto global no SystemRuntimeConfig
   *  (`mobile.consumer.booking.window_days_*`). Servido ao app no detail público. */
  bookingWindowDays?: number;
  /** Agenda Personalizada. O backend público devolve sempre resolvido
   *  (defaults = tudo OFF); o booking gateia toda UX nova em `enabled`. */
  customSchedule?: CustomScheduleSettings;
  /** Preferências de agendamento. Resolvidas pelo backend público. */
  booking?: {
    /** Liga a opção "Qualquer um" no booking (vale para 1 ou N barbeiros).
     *  Default da plataforma DESLIGADO; quando desligado, com 1 barbeiro o passo
     *  de profissional é pulado. */
    anyBarberEnabled?: boolean;
    /** Liga o fundo ambiente (fotos rotativas) na agenda pública web. Default da
     *  plataforma LIGADO; o dono desliga para um ambiente limpo. */
    ambienceEnabled?: boolean;
  };
  /** Cor de destaque (brand) do tenant: 'blue' (default) | 'purple' | '#RRGGBB'
   *  personalizado. Aplicada globalmente (painel + booking) via data-accent. */
  accentColor?: string;
}

/** Opcoes + default (min) de um parametro do motor de agenda (SSOT: SystemRuntimeConfig). */
export interface BookingConfigOption {
  default: number;
  options: number[];
}

/** Opcoes dos selects de `/dashboard/schedules`, servidas pelo getMe (tenant-me). */
export interface BookingConfigMeta {
  slotGranularity: BookingConfigOption;
  bufferBetweenServices: BookingConfigOption;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  address?: string;
  phone?: string;
  /** Geolocalizacao (graus decimais). Base para busca por raio no app do cliente. */
  latitude?: number | null;
  longitude?: number | null;
  logo?: string | null;
  logoAssetId?: string | null;
  logoUrl?: string | null;
  settings?: TenantSettings;
  /** Opcoes + defaults dos selects de agenda (granularidade/buffer). So no getMe. */
  bookingConfig?: BookingConfigMeta;
  /** Libera o card "Limpar dados de teste" (zona de perigo). Controlado pelo Super Admin. */
  allowTestDataReset?: boolean;
  /** Horários de disparo do resumo diário de agenda para os barbeiros via WhatsApp. Array de "HH:MM". Vazio = desativado. */
  summaryNotificationTimes?: string[];
  /** Horários de disparo do resumo do dia atual (intraday) para os barbeiros. Array de "HH:MM". Vazio = desativado. */
  intradaySummaryTimes?: string[];
  /** Antecedência (min) do lembrete do próximo atendimento do barbeiro. Null = desligado. */
  nextAppointmentReminderMinutes?: number | null;
  /** Envia WhatsApp ao dono quando cliente agenda. Default: true. */
  notifyOwnerOnBooking?: boolean;
  /** Minutos para auto-confirmar agendamento PENDING sem ação manual. Null = usa default da plataforma. */
  autoConfirmMinutes?: number | null;
  /** Instrução de tom adicional para o bot desta barbearia. Null = padrão da plataforma. */
  botPersonaHint?: string | null;
  /** Horário de funcionamento em texto livre. Exibido pelo chatbot. Null = bot responde "Consulte diretamente na barbearia". */
  businessHours?: string | null;
}

export interface AvailabilitySlot {
  barberId: string;
  barberName: string;
  barberPhoto: string | null;
  startTime: string;
  endTime: string;
}

export const publicApi = {
  getTenant: (slug: string) => api.get<Tenant>(`/public/tenants/${slug}`),

  /** Disponibilidade de slug ao vivo no SlugPreview do /register. */
  checkSlugAvailability: (slug: string) =>
    api.get<{
      available: boolean;
      reason?: 'invalid' | 'reserved' | 'taken';
      suggestion?: string;
    }>('/platform/check-slug', { params: { slug } }),

  /** Status de provedores OAuth disponiveis. Front esconde o botao quando off. */
  getOAuthStatus: () =>
    api.get<{ google: boolean }>('/platform/oauth-status'),

  /** Configuracao publica da plataforma. Endpoint nao requer auth. Use em
   *  paginas marketing/pricing pra ler `trialDays`, branding etc. */
  getPlatformConfig: () =>
    api.get<PlatformConfigRow>('/platform/config'),

  /** URLs das lojas dos apps mobile (RuntimeConfig). Vazio = ainda nao publicado. */
  getMobileLanding: () =>
    api.get<{ androidUrl: string; iosUrl: string; webBookingEnabled: boolean }>(
      '/platform/mobile-landing',
    ),

  /**
   * Resolve token curto enviado pelo chatbot via WhatsApp
   * (`/booking/<slug>?t=<id>`). Devolve `{ phone, name }` para o
   * frontend pré-preencher o formulário do agendamento.
   */
  resolveBookingToken: (id: string) =>
    api.get<{ phone: string; name: string; email?: string }>(
      `/public/tenants/booking-tokens/${id}`,
    ),

  /** Lookup do cliente por telefone (estilo CEP): devolve o nome se ja for
   *  cliente da barbearia, pra pre-preencher (editavel). null se nao achar. */
  lookupCustomer: (slug: string, phone: string) =>
    api.get<{ name: string | null; email: string | null }>(
      `/public/tenants/${slug}/customer-lookup`,
      { params: { phone } },
    ),

  /** Com a Agenda Personalizada ligada, `barberId` filtra os serviços do
   *  profissional (generalista vê todos). Desligada, o backend ignora. */
  getServices: (slug: string, params?: { barberId?: string }) =>
    api.get<Service[]>(`/public/tenants/${slug}/services`, { params }),

  /** Com a Agenda Personalizada ligada, `serviceId` filtra os profissionais
   *  vinculados + generalistas. Desligada, o backend ignora. */
  getBarbers: (slug: string, params?: { serviceId?: string }) =>
    api.get<Barber[]>(`/public/tenants/${slug}/barbers`, { params }),

  getAvailability: (
    slug: string,
    params: { barberId?: string; serviceId: string; date: string },
  ) => api.get<AvailabilitySlot[]>(`/public/tenants/${slug}/availability`, { params }),

  /** Dias fechados da barbearia no range do calendário público (folga geral,
   *  dia sem expediente de todos os barbeiros ou do barbeiro filtrado). O
   *  strip de datas desabilita esses dias. `days` vem do backend
   *  (SystemRuntimeConfig booking.calendar_days). */
  getClosedDays: (slug: string, params?: { from?: string; to?: string; barberId?: string }) =>
    api.get<{ from: string; days: number; closedDates: string[] }>(
      `/public/tenants/${slug}/closed-days`,
      { params },
    ),

  createAppointment: (
    slug: string,
    data: {
      barberId: string;
      serviceId: string;
      customerName: string;
      customerPhone: string;
      customerEmail?: string;
      date: string;
      startTime: string;
      /** Prova de telefone verificado por OTP - obrigatorio so para cliente NOVO. */
      verifiedChallengeId?: string;
    },
  ) => api.post<Appointment>(`/public/tenants/${slug}/appointments`, data),

  /** Dispara o codigo de verificacao do telefone (cliente novo na agenda web).
   *  Devolve o `channel` (whatsapp/sms) pra UI nomear por onde chega o codigo. */
  requestBookingOtp: (slug: string, phone: string) =>
    api.post<{
      challengeId: string;
      expiresAt: string;
      cooldownSeconds: number;
      channel: 'whatsapp' | 'sms';
    }>(`/public/tenants/${slug}/booking/otp/request`, { phone }),

  /** Verifica o codigo digitado; em sucesso o challenge fica consumido e o
   *  `challengeId` vira a prova (verifiedChallengeId) do createAppointment. */
  verifyBookingOtp: (slug: string, challengeId: string, code: string) =>
    api.post<{ verified: true; challengeId: string }>(
      `/public/tenants/${slug}/booking/otp/verify`,
      { challengeId, code },
    ),
};

// ─── Reports ──────────────────────────────────────────────────────────────────

export interface RevenueReport {
  from: string;
  to: string;
  summary: {
    scheduledValue: number;
    completedValue: number;
    receivableValue: number;
    undecidedValue: number;
    nonRevenueValue: number;
    waivedValue: number;
    courtesyValue: number;
    cancelledValue: number;
    noShowValue: number;
    scheduledCount: number;
    completedCount: number;
    receivableCount: number;
    undecidedCount: number;
    nonRevenueCount: number;
    waivedCount: number;
    courtesyCount: number;
    cancelledCount: number;
    noShowCount: number;
    totalAppointments: number;
    avgDurationMinutes: number;
    measuredDurationCount: number;
    estimatedDurationCount: number;
    operationalCompletedCount: number;
    completionRate: number;
  };
  byBarber: {
    barberId: string;
    barberName: string;
    scheduledValue: number;
    completedValue: number;
    scheduledCount: number;
    completedCount: number;
  }[];
  byService: {
    serviceId: string;
    serviceName: string;
    scheduledValue: number;
    completedValue: number;
    scheduledCount: number;
    completedCount: number;
  }[];
  dailyBreakdown: {
    date: string;
    scheduledValue: number;
    completedValue: number;
    receivableValue: number;
    undecidedValue: number;
    nonRevenueValue: number;
    waivedValue: number;
    courtesyValue: number;
    cancelledValue: number;
    noShowValue: number;
    scheduledCount: number;
    completedCount: number;
    receivableCount: number;
    undecidedCount: number;
    nonRevenueCount: number;
    waivedCount: number;
    courtesyCount: number;
    cancelledCount: number;
    noShowCount: number;
  }[];
}

export interface AppointmentStatsReport {
  total: number;
  byStatus: Partial<Record<AppointmentStatus, number>>;
  conversionRate: number;
  avgDuration: number;
}

export interface FinancialAppointmentRow extends Appointment {}

export interface BarberPerformanceReport {
  barberId: string;
  barberName: string;
  total: number;
  revenue: number;
  avgRating?: number;
}

export const reportsApi = {
  getFinancialSummary: (params: { from: string; to: string; barberId?: string }) =>
    api.get<RevenueReport>('/reports/financial-summary', { params }),

  getFinancialAppointments: (params: {
    from: string;
    to: string;
    barberId?: string;
    status?:
      | 'paid'
      | 'receivable'
      | 'undecided'
      | 'non_revenue'
      | 'waived'
      | 'courtesy'
      | 'scheduled'
      | 'cancelled'
      | 'no_show'
      | 'all';
    search?: string;
    phone?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
  }) =>
    api
      .get<PaginatedResponse<FinancialAppointmentRow>>('/reports/financial-appointments', {
        params,
      })
      .then(normalizeAppointmentResponse),

  getAppointmentStats: (params: { from: string; to: string }) =>
    api.get<AppointmentStatsReport>('/reports/appointments', { params }),

  getBarberPerformance: () =>
    api.get<BarberPerformanceReport[]>('/reports/barbers'),
};

// ─── Tenants ──────────────────────────────────────────────────────────────────

export interface GeocodeResult {
  latitude: number;
  longitude: number;
  displayName: string;
}

export interface CepResult {
  cep: string;
  street: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
}

/** Geocodificacao endereco -> coordenadas + lookup de CEP (ViaCEP no backend). */
export const geoApi = {
  geocode: (q: string) => api.get<GeocodeResult>('/geocode', { params: { q } }),
  lookupCep: (cep: string) =>
    api.get<CepResult>(`/geocode/cep/${cep.replace(/\D/g, '')}`),
};

/** Contagens por tabela-fato que o reset de dados de teste apaga (U10). */
export interface TestDataResetCounts {
  appointments: number;
  reassignmentOffers: number;
  stockMovements: number;
  loyaltyTransactions: number;
  pendingConfirmations: number;
  endUserNotifications: number;
  conversations: number;
  conversationMessages: number;
  outboundMessages: number;
}

export interface TestDataResetPreview {
  counts: TestDataResetCounts;
  total: number;
}

export const tenantsApi = {
  getMe: () => api.get<Tenant>('/tenants/me'),

  update: (data: Record<string, unknown>) =>
    api.put<Tenant>('/tenants/me', data),

  uploadLogo: (file: File) => {
    const form = new FormData();
    form.append('logo', file);
    return api.post<{ logo: string | null; logoUrl?: string | null }>(
      '/tenants/me/logo',
      form,
      {
      headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
  },

  /** U10 - preview (read-only) do que o reset de dados de teste apagaria. */
  testDataResetPreview: () =>
    api.get<TestDataResetPreview>('/tenants/me/test-data-reset/preview'),

  /** U10 - executa o reset. `confirmation` deve ser igual ao slug do tenant. */
  testDataReset: (payload: { confirmation: string; reason?: string }) =>
    api.post<TestDataResetPreview>('/tenants/me/test-data-reset', payload),
};

// ─── Users ────────────────────────────────────────────────────────────────────

export interface User {
  id: string;
  name: string;
  email: string;
  /** Telefone pessoal. Para SUPER_ADMIN, esse phone passa pelo Guard 3 do
   *  chatbot - permite testar tenants do telefone pessoal sem bloqueio. */
  phone?: string | null;
  role: 'OWNER' | 'ADMIN' | 'BARBER' | 'SUPER_ADMIN';
  active: boolean;
  themePreference?: ThemePreferenceId | null;
  sidebarPreference?: SidebarPreferenceId | null;
  crudModalSidebarPreference?: SidebarPreferenceId | null;
  defaultTablePageSize?: number | null;
  tableDensityPreference?: TableDensity | null;
  appointmentViewMode?: AppointmentViewMode | null;
  createdAt: string;
}

export const usersApi = {
  getAll: () => api.get<User[]>('/users'),

  create: (data: { name: string; email: string; password: string; role: User['role'] }) =>
    api.post<User>('/users', data),

  update: (id: string, data: Partial<User>) => api.put<User>(`/users/${id}`, data),

  delete: (id: string) => api.delete(`/users/${id}`),
};

// ─── Profile ──────────────────────────────────────────────────────────────────

export type ThemePreferenceId =
  | '7cors-client'
  | '7cors-light'
  | '7cors-dark'
  | '7cors-system';

/** Estado padrao da sidebar para o usuario logado. Persistido em
 *  User.sidebarPreference (PostgreSQL). Null = padrao da plataforma
 *  (expanded). Atualizado tanto pelo cartao de preferencias quanto pelo
 *  toggle (chevron) das sidebars (/dashboard e /admin). */
export type SidebarPreferenceId = 'expanded' | 'collapsed';

/** Densidade padrao das tabelas do "Cartao completo" (DsTableCard) para o
 *  usuario logado. Persistido em User.tableDensityPreference (PostgreSQL).
 *  Null = comportamento padrao ('comfortable'). Mesma mecanica do
 *  crudModalSidebarPreference/defaultTablePageSize. */
export type TableDensity = 'compact' | 'comfortable';

/** Modo de visualizacao da Agenda (/dashboard/appointments), por-usuario. */
export type AppointmentViewMode = 'timeline' | 'lista';

export const profileApi = {
  getMe: () => api.get<User>('/users/me'),

  updateMe: (data: {
    name?: string;
    email?: string;
    /** Telefone pessoal (SUPER_ADMIN ganha bypass do Guard 3). String
     *  livre - backend normaliza pra so digitos antes de gravar. null
     *  ou string vazia limpa. */
    phone?: string | null;
  }) => api.put<User>('/users/me', data),

  /** Atualiza preferencias do usuario logado no PostgreSQL.
   *  Aceita tema, tema visual efetivo (quando o tema escolhido e
   *  '7cors-system') e estado padrao da sidebar. */
  updatePreferences: (data: {
    themePreference?: ThemePreferenceId | null;
    systemThemePreference?: '7cors-light' | '7cors-dark' | null;
    sidebarPreference?: SidebarPreferenceId | null;
    crudModalSidebarPreference?: SidebarPreferenceId | null;
    defaultTablePageSize?: number | null;
    tableDensityPreference?: TableDensity | null;
    appointmentViewMode?: AppointmentViewMode | null;
  }) =>
    api.patch<{
      id: string;
      themePreference: ThemePreferenceId | null;
      sidebarPreference: SidebarPreferenceId | null;
      crudModalSidebarPreference: SidebarPreferenceId | null;
      defaultTablePageSize: number | null;
      tableDensityPreference: TableDensity | null;
      appointmentViewMode: AppointmentViewMode | null;
    }>('/users/me/preferences', data),

  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.put('/users/me/password', data),
};

// ─── WhatsApp ─────────────────────────────────────────────────────────────────

export interface WhatsAppStatus {
  connected: boolean;
  phone?: string;
  name?: string;
}

export interface WhatsAppQrCode {
  qrcode?: string | null;
  pairingCode?: string | null;
  connected: boolean;
  state?: 'open' | 'pending' | 'connecting' | 'close' | 'error' | string;
  message?: string;
}

export const whatsappApi = {
  getStatus: () => api.get<WhatsAppStatus>('/whatsapp/status'),
  getQrCode: (trigger?: boolean) =>
    api.get<WhatsAppQrCode>('/whatsapp/qrcode', {
      params: trigger ? { trigger: 'true' } : {},
    }),
  disconnect: () => api.post('/whatsapp/disconnect'),
};

// ─── Conversations ────────────────────────────────────────────────────────────

export interface ConversationMessage {
  id: string;
  sender: 'CUSTOMER' | 'BOT' | 'AGENT';
  senderName?: string;
  content: string;
  createdAt: string;
}

export interface ConversationBlockInfo {
  reason: string | null;
  blockedBy: string | null;
  createdAt: string;
}

export interface Conversation {
  id: string;
  status: 'BOT' | 'QUEUE' | 'ACTIVE' | 'RESOLVED';
  messagesCount: number;
  customer: { id: string; name: string; phone: string };
  messages?: ConversationMessage[];
  /** Ultima mensagem da conversa (preenchida pela LISTA; `messages` vem vazio la). */
  lastMessage?: ConversationMessage | null;
  humanTakeoverAt?: string;
  resolvedAt?: string;
  createdAt: string;
  updatedAt: string;
  blocked?: boolean;
  block?: ConversationBlockInfo | null;
  botPausedUntil?: string | null;
  botPausedIndefinitely?: boolean;
  lastReadAt?: string | null;
  /** true quando a ultima mensagem do cliente chegou apos a ultima leitura. */
  unread?: boolean;
  /** Quantidade de mensagens do cliente nao lidas (0 quando lida/respondida). */
  unreadCount?: number;
}

/** Elegibilidade + janela de 24h para iniciar uma conversa outbound. */
export interface OutboundPreview {
  eligible: boolean;
  phone: string | null;
  lastCustomerMessageAt: string | null;
  existingConversationId: string | null;
  existingStatus: Conversation['status'] | null;
}

/** Opcoes dinamicas do seletor de pausa do bot (vem do SystemRuntimeConfig). */
export interface ConversationPauseConfig {
  options: number[];
  allowIndefinite: boolean;
  default: number;
}

export const conversationsApi = {
  getAll: (params?: { status?: string }) =>
    api.get<Conversation[]>('/conversations', { params }),

  getOne: (id: string) => api.get<Conversation>(`/conversations/${id}`),

  // botPausedDuration: minutos; null = indefinido; undefined = mantem/default ao assumir
  sendMessage: (id: string, content: string, botPausedDuration?: number | null) =>
    api.post(`/conversations/${id}/messages`, {
      content,
      ...(botPausedDuration !== undefined ? { botPausedDuration } : {}),
    }),

  resolve: (id: string) => api.put(`/conversations/${id}/resolve`),

  /** Devolve o atendimento ao bot (status BOT, zera pausa). */
  handback: (id: string) => api.put(`/conversations/${id}/handback`),

  /** Reagenda a janela de pausa do bot numa conversa ACTIVE (minutos; null = indefinido). */
  setPause: (id: string, botPausedDuration: number | null) =>
    api.put(`/conversations/${id}/pause`, { botPausedDuration }),

  /** Exclui a conversa inteira (registro + mensagens). So OWNER/ADMIN. */
  delete: (id: string) => api.delete(`/conversations/${id}`),

  /** Inicia (ou reabre) conversa outbound com um cliente do cadastro. */
  initiate: (body: {
    customerId: string;
    message: string;
    botPausedDuration?: number | null;
  }) => api.post<{ conversationId: string }>('/conversations/initiate', body),

  /** Preview de elegibilidade + janela 24h ao selecionar um cliente. */
  outboundPreview: (customerId: string) =>
    api.get<OutboundPreview>('/conversations/outbound/preview', {
      params: { customerId },
    }),

  /** Config dinamica das opcoes de pausa do bot. */
  pauseConfig: () =>
    api.get<ConversationPauseConfig>('/conversations/pause-config'),

  block: (id: string, reason?: string) =>
    api.post<{ ok: boolean; block: ConversationBlockInfo }>(
      `/conversations/${id}/block`,
      { reason },
    ),

  unblock: (id: string) =>
    api.delete<{ ok: boolean }>(`/conversations/${id}/block`),
};

// ─── Barber Portal ────────────────────────────────────────────────────────────

export interface BarberPortalAppointment {
  id: string;
  scheduledAt: string;
  endsAt: string;
  status: AppointmentStatus;
  customerName: string;
  customerPhone: string;
  service: { id: string; name: string; duration: number; price: number };
  /** Vencido (fuso BR) - calculado pelo backend. */
  isOverdue?: boolean;
  /** Ações operacionais do barbeiro (iniciar/concluir) - fonte de verdade do backend. */
  actions?: AppointmentLifecycleAction[];
}

export interface BarberPortalToday {
  barber: { id: string; name: string; photoUrl?: string };
  appointments: BarberPortalAppointment[];
  summary: { total: number; completed: number; pending: number; inProgress: number };
}

export interface BarberPortalService {
  id: string;
  name: string;
  duration: number;
  price: number;
}

export interface WalkInData {
  customerPhone: string;
  customerName?: string;
  serviceId: string;
}

export const barberPortalApi = {
  getToday: () => api.get<BarberPortalToday>('/barber-portal/today'),
  getServices: () => api.get<BarberPortalService[]>('/barber-portal/services'),
  registerWalkIn: (data: WalkInData) =>
    api.post('/barber-portal/walk-in', data),
  startAppointment: (id: string) => api.put(`/barber-portal/appointments/${id}/start`),
  completeAppointment: (id: string) => api.put(`/barber-portal/appointments/${id}/complete`),
};

// ─── Inventory (produtos + movimentacoes de estoque) ───────────────────────

export type ProductCategory = 'RESALE' | 'INTERNAL' | 'BOTH';

export interface Product {
  id: string;
  tenantId: string;
  name: string;
  sku: string | null;
  description: string | null;
  category: ProductCategory;
  unit: string;
  costPrice: number;
  salePrice: number | null;
  minStock: number;
  active: boolean;
  currentStock: number;
  photoAssetId?: string | null;
  photoUrl?: string | null;
  imageUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProductPayload {
  name: string;
  sku?: string;
  description?: string;
  category?: ProductCategory;
  unit?: string;
  costPrice?: number;
  salePrice?: number;
  minStock?: number;
  active?: boolean;
}

/** KPIs do topo da aba Produtos (contados no backend). */
export interface ProductStats {
  active: number;
  resale: number;
  lowStock: number;
}

export const productsApi = {
  getAll: (params?: { active?: boolean; category?: ProductCategory; search?: string }) =>
    api.get<Product[]>('/products', { params }),
  /** Lista SERVER-SIDE (tabela do "Cartao completo"): so a pagina pedida volta.
   *  Filtros/ordenacao no banco. `getAll` (sem page) segue pros dropdowns. */
  getPaged: (params: {
    page: number;
    pageSize: number;
    search?: string;
    category?: ProductCategory;
    status?: 'ativo' | 'inativo';
    sortBy?: 'name' | 'costPrice' | 'salePrice' | 'minStock';
    sortDir?: 'asc' | 'desc';
  }) => api.get<PagedResult<Product>>('/products', { params }),
  /** Contagens dos KPIs (ativos / revenda / abaixo do minimo), calculadas no banco. */
  getStats: () => api.get<ProductStats>('/products/stats'),
  getOne: (id: string) => api.get<Product>(`/products/${id}`),
  create: (data: CreateProductPayload) => api.post<Product>('/products', data),
  update: (id: string, data: Partial<CreateProductPayload>) =>
    api.put<Product>(`/products/${id}`, data),
  remove: (id: string) => api.delete(`/products/${id}`),
  uploadImage: (id: string, file: File) => {
    const form = new FormData();
    form.append('photo', file);
    return api.post<Product>(`/products/${id}/photo`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export type MovementType = 'ENTRY' | 'SALE' | 'INTERNAL_USE' | 'ADJUSTMENT';

export interface StockMovement {
  id: string;
  tenantId: string;
  productId: string;
  type: MovementType;
  quantity: number;
  unitPrice: number | null;
  totalValue: number | null;
  reason: string | null;
  supplier: string | null;
  appointmentId: string | null;
  /** Cliente vinculado a venda no balcao (type=SALE). Null nos demais tipos. */
  customerId: string | null;
  userId: string | null;
  createdAt: string;
  product?: { id: string; name: string; unit: string; sku: string | null };
  customer?: {
    id: string;
    name: string;
    phone?: string | null;
    email?: string | null;
  } | null;
  serviceId?: string | null;
  service?: { id: string; name: string } | null;
}

export interface CreateMovementPayload {
  productId: string;
  type: MovementType;
  quantity: number;
  unitPrice?: number;
  reason?: string;
  supplier?: string;
  appointmentId?: string;
  /** So aplicado quando type=SALE: vincula a venda a um cliente existente. */
  customerId?: string;
  /** So aplicado quando type=INTERNAL_USE: vincula o consumo a um servico. */
  serviceId?: string;
}

export interface MovementsListResponse {
  items: StockMovement[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export const stockMovementsApi = {
  getAll: (params?: {
    productId?: string;
    /** Busca server-side por NOME do produto (filtra a tabela de movimentacoes). */
    search?: string;
    type?: MovementType;
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
  }) => api.get<MovementsListResponse>('/stock-movements', { params }),
  /** Totalizadores (entradas/saidas/total) do escopo filtrado - cards do topo. */
  getSummary: (params?: {
    productId?: string;
    search?: string;
    from?: string;
    to?: string;
  }) => api.get<MovementsSummary>('/stock-movements/summary', { params }),
  create: (data: CreateMovementPayload) =>
    api.post<StockMovement>('/stock-movements', data),
  update: (id: string, data: UpdateMovementPayload) =>
    api.put<StockMovement>(`/stock-movements/${id}`, data),
  remove: (id: string) => api.delete(`/stock-movements/${id}`),
};

export interface UpdateMovementPayload {
  type?: MovementType;
  quantity?: number;
  unitPrice?: number | null;
  reason?: string | null;
  supplier?: string | null;
  customerId?: string | null;
  serviceId?: string | null;
}

export interface MovementTypeTotals {
  count: number;
  value: number;
  quantity: number;
}

export interface MovementsSummary {
  byType: Record<MovementType, MovementTypeTotals>;
  totalCount: number;
  totalValue: number;
}

export interface InventoryOverviewReport {
  stockValue: {
    totalCost: number;
    totalResaleValue: number;
    potentialMargin: number;
  };
  lowStock: Array<{
    productId: string;
    name: string;
    unit: string;
    currentStock: number;
    minStock: number;
  }>;
  /** Indicadores por tipo de movimento no periodo (count + quantidade + valor). */
  byType: Record<MovementType, { count: number; quantity: number; value: number }>;
  consumption: Array<{
    productId: string;
    name: string;
    unit: string;
    sale: number;
    internal: number;
    adjustmentOut: number;
    valueOut: number;
    totalOut: number;
  }>;
  sales: Array<{
    productId: string;
    name: string;
    qty: number;
    revenue: number;
  }>;
  totals: {
    salesRevenue: number;
    entryCost: number;
    salesCount: number;
    consumptionCount: number;
  };
}

export const inventoryReportsApi = {
  overview: (params?: { from?: string; to?: string; type?: MovementType }) =>
    api.get<InventoryOverviewReport>('/inventory-reports/overview', { params }),
};

export interface ServiceProductLink {
  id: string;
  productId: string;
  quantity: number;
  product: {
    id: string;
    name: string;
    unit: string;
    category: ProductCategory;
    active: boolean;
  };
}

export const serviceProductsApi = {
  list: (serviceId: string) =>
    api.get<ServiceProductLink[]>(`/service-products/${serviceId}`),
  set: (serviceId: string, items: { productId: string; quantity: number }[]) =>
    api.put<ServiceProductLink[]>(`/service-products/${serviceId}`, { items }),
};

// ─── Marketing Campaigns ──────────────────────────────────────────────────

export type CampaignChannel = 'WHATSAPP';
export type SegmentType =
  | 'ALL'
  | 'ACTIVE'
  | 'INACTIVE'
  | 'BIRTHDAY_MONTH'
  | 'MANUAL';
export type ScheduleType =
  | 'IMMEDIATE'
  | 'ONE_TIME_SCHEDULED'
  | 'RECURRING'
  | 'AUTO_BIRTHDAY';
export type CampaignStatus =
  | 'DRAFT'
  | 'SCHEDULED'
  | 'SENDING'
  | 'SENT'
  | 'ACTIVE'
  | 'PAUSED'
  | 'FAILED'
  | 'CANCELLED';

export interface CampaignRecurrence {
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  dayOfWeek?: number;
  dayOfMonth?: number;
  hour: number;
  minute: number;
}

export interface Campaign {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  message: string;
  channel: CampaignChannel;
  segmentType: SegmentType;
  segmentFilters: { customerIds?: string[] } | null;
  scheduleType: ScheduleType;
  scheduledAt: string | null;
  recurrence: CampaignRecurrence | null;
  nextRunAt: string | null;
  lastRunAt: string | null;
  status: CampaignStatus;
  active: boolean;
  stats: { queued: number; sent: number; failed: number; total: number } | null;
  createdAt: string;
  updatedAt: string;
  totalDeliveries?: number;
}

export interface CreateCampaignPayload {
  name: string;
  description?: string;
  message: string;
  channel?: CampaignChannel;
  segmentType: SegmentType;
  segmentFilters?: { customerIds?: string[] };
  scheduleType: ScheduleType;
  scheduledAt?: string;
  recurrence?: CampaignRecurrence;
}

export interface SegmentPreview {
  total: number;
  sample: Array<{ id: string; name: string; phone: string }>;
}

export const campaignsApi = {
  getAll: (params?: { status?: CampaignStatus }) =>
    api.get<Campaign[]>('/campaigns', { params }),
  getOne: (id: string) => api.get<Campaign>(`/campaigns/${id}`),
  create: (data: CreateCampaignPayload) =>
    api.post<Campaign>('/campaigns', data),
  update: (id: string, data: Partial<CreateCampaignPayload> & { active?: boolean }) =>
    api.put<Campaign>(`/campaigns/${id}`, data),
  remove: (id: string) => api.delete(`/campaigns/${id}`),
  send: (id: string) =>
    api.post<{ queued: number; sent: number; failed: number }>(
      `/campaigns/${id}/send`,
    ),
  previewSegment: (data: { segmentType: SegmentType; customerIds?: string[] }) =>
    api.post<SegmentPreview>('/campaigns/preview-segment', data),
};

/** Resultado do disparo manual ("Enviar agora") dos avisos ao barbeiro. */
export interface IntradayDispatchSummary {
  enviados: number;
  pulados: number;
}

export const notificationsApi = {
  /** Dispara o resumo do dia atual para todos os barbeiros ativos, agora. */
  sendIntradaySummaryNow: () =>
    api.post<IntradayDispatchSummary>(
      '/notifications/barber-intraday-summary/send-now',
    ),
  /** Dispara o lembrete do próximo atendimento para todos os barbeiros ativos, agora. */
  sendNextReminderNow: () =>
    api.post<IntradayDispatchSummary>(
      '/notifications/barber-next-reminder/send-now',
    ),
};

export default api;
